'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Crown, Building2, FileText, CreditCard, MessageSquare, BarChart2, CheckCircle, Search, Eye, RefreshCw, Loader2, Check, Package, LogOut, Plus, Edit2, TrendingUp, Users, Save, Activity, AlertTriangle, Server, Database, Globe, Zap, DollarSign, ArrowUpRight, ArrowDownRight, Bell, ChevronRight, BarChart, Phone, XCircle, Info, HardDrive, Wifi, Lock, Target, Layers, AlertCircle, PlayCircle, PauseCircle, Clock, Star, ShieldCheck, ShieldAlert, CloudOff, CheckSquare, Percent, MapPin, ArrowRight, ArrowLeft, Home, Mail, Shield, Key } from 'lucide-react';
import AuthAdminDashboard from './AuthAdminDashboard';
import AuthAlertCenter from './AuthAlertCenter';
import AdminActionLogsPanel from './AdminActionLogsPanel';
import PlatformUsersPanel from './PlatformUsersPanel';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';
import { NexoraMark } from '@/components/ui/AppLogo';
import { type Language, useTranslation } from '@/lib/i18n';
import Icon from '@/components/ui/AppIcon';
import { useRouter } from 'next/navigation';


type AdminTab = 'overview' | 'companies' | 'join_requests' | 'subscriptions' | 'complaints' | 'packages' | 'analytics' | 'system' | 'revenue' | 'contact_messages' | 'auth_admin' | 'auth_alerts' | 'admin_logs' | 'platform_users';

interface Company {
  id: string;
  name_ar: string;
  name_en: string;
  cr_number: string;
  vat_number: string;
  city: string;
  phone: string;
  email: string;
  is_active: boolean;
  subscription_status: string;
  created_at: string;
  max_users: number;
  max_branches?: number;
  subscription_ends_at?: string;
  is_read_only?: boolean;
  read_only_reason?: string;
}

interface JoinRequest {
  id: string;
  company_name_ar: string;
  company_name_en: string;
  cr_number: string;
  vat_number: string;
  city: string;
  phone: string;
  admin_name: string;
  admin_email: string;
  status: string;
  reviewer_notes: string;
  created_at: string;
  requested_package_id?: string;
  requested_package_name?: string;
  cr_document_url?: string;
  vat_document_url?: string;
  national_address_document_url?: string;
  id_document_url?: string;
  payment_proof_url?: string;
}

interface Complaint {
  id: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  created_at: string;
  company_id: string;
}

interface ContactMessage {
  id: string;
  name: string;
  phone: string;
  email: string;
  subject: string;
  description: string;
  attachment_url?: string;
  attachment_name?: string;
  status: string;
  admin_notes?: string;
  created_at: string;
}

interface Package {
  id: string;
  name_ar: string;
  name_en: string;
  price_monthly: number;
  price_yearly: number;
  price_usd_monthly?: number;
  price_eur_monthly?: number;
  max_users: number;
  max_branches: number;
  features: string[];
  is_active: boolean;
  is_popular?: boolean;
  sort_order: number;
}

const defaultPackages: Package[] = [
  {
    id: 'pkg-store', name_ar: 'نيكسورا ستور', name_en: 'Nexora Store',
    price_monthly: Math.round(999 / 12), price_yearly: 999,
    price_usd_monthly: Math.round(266 / 12), price_eur_monthly: Math.round(245 / 12),
    max_users: 5, max_branches: 2,
    features: ['نقطة البيع (POS) كاملة', 'المبيعات والفواتير الإلكترونية', 'المشتريات وأوامر الشراء', 'إدارة المخزون والمستودعات', 'إدارة المرتجعات', 'إدارة الكاش والصناديق', 'التقارير التشغيلية', 'زاتكا المرحلة الأولى', 'تطبيق PWA'],
    is_active: true, is_popular: false, sort_order: 1,
  },
  {
    id: 'pkg-business', name_ar: 'نيكسورا بيزنس', name_en: 'Nexora Business',
    price_monthly: Math.round(2499 / 12), price_yearly: 2499,
    price_usd_monthly: Math.round(666 / 12), price_eur_monthly: Math.round(612 / 12),
    max_users: 15, max_branches: 5,
    features: ['كل ميزات ستور +', 'المحاسبة العامة الكاملة', 'دليل الحسابات والأستاذ العام', 'القيود اليومية وميزان المراجعة', 'قائمة الدخل والميزانية العمومية', 'الذمم المدينة والدائنة', 'ضريبة القيمة المضافة (VAT)', 'زاتكا المرحلة الثانية (XML + QR)', 'سندات القبض والصرف', 'التقارير المالية الكاملة', 'استيراد CSV', 'النسخ الاحتياطي التلقائي'],
    is_active: true, is_popular: true, sort_order: 2,
  },
  {
    id: 'pkg-professional', name_ar: 'نيكسورا برو', name_en: 'Nexora Professional',
    price_monthly: Math.round(4999 / 12), price_yearly: 4999,
    price_usd_monthly: Math.round(1333 / 12), price_eur_monthly: Math.round(1225 / 12),
    max_users: 50, max_branches: 20,
    features: ['كل ميزات بيزنس +', 'متعدد الشركات (3 شركات)', 'مراكز التكلفة والأرباح', 'الأصول الثابتة والاستهلاك', 'سير عمل الموافقات المتقدمة', 'صلاحيات وأدوار مخصصة', 'لوحات KPI التنفيذية', 'سجلات التدقيق الكاملة', 'مراقبة صحة النظام', 'بوابة الشركة المخصصة', 'دعم أولوية 24/7'],
    is_active: true, is_popular: false, sort_order: 3,
  },
  {
    id: 'pkg-addon-hr', name_ar: 'إضافة: الموارد البشرية والرواتب', name_en: 'Add-on: HR & Payroll',
    price_monthly: Math.round(999 / 12), price_yearly: 999,
    price_usd_monthly: Math.round(266 / 12), price_eur_monthly: Math.round(245 / 12),
    max_users: 0, max_branches: 0,
    features: ['ملفات الموظفين والعقود', 'الحضور والانصراف', 'إدارة الإجازات', 'مسير الرواتب', 'تقارير الرواتب والضرائب'],
    is_active: true, is_popular: false, sort_order: 4,
  },
  {
    id: 'pkg-addon-mfg', name_ar: 'إضافة: التصنيع والإنتاج', name_en: 'Add-on: Manufacturing',
    price_monthly: Math.round(1999 / 12), price_yearly: 1999,
    price_usd_monthly: Math.round(533 / 12), price_eur_monthly: Math.round(490 / 12),
    max_users: 0, max_branches: 0,
    features: ['قوائم المواد (BOM)', 'أوامر الإنتاج', 'أوامر العمل', 'تتبع الإنتاج', 'تكاليف التصنيع الكاملة'],
    is_active: true, is_popular: false, sort_order: 5,
  },
  {
    id: 'pkg-addon-integrations', name_ar: 'إضافة: التكاملات الذكية', name_en: 'Add-on: Smart Integrations',
    price_monthly: Math.round(799 / 12), price_yearly: 799,
    price_usd_monthly: Math.round(213 / 12), price_eur_monthly: Math.round(196 / 12),
    max_users: 0, max_branches: 0,
    features: ['تكامل واتساب للإشعارات', 'وصول API كامل', 'تكاملات خارجية', 'إشعارات متقدمة', 'ربط الأنظمة الخارجية'],
    is_active: true, is_popular: false, sort_order: 6,
  },
];

const statusColors: Record<string, string> = {
  pending: 'badge-warning', approved: 'badge-success', rejected: 'badge-danger',
  info_required: 'badge-info', active: 'badge-success', trial: 'badge-primary',
  suspended: 'badge-danger', open: 'badge-warning', in_progress: 'badge-info',
  resolved: 'badge-success', closed: 'badge-neutral',
};

const statusLabels: Record<string, { ar: string; en: string }> = {
  pending:       { ar: 'قيد المراجعة',   en: 'Pending' },
  approved:      { ar: 'مقبول',           en: 'Approved' },
  rejected:      { ar: 'مرفوض',           en: 'Rejected' },
  info_required: { ar: 'يحتاج معلومات',  en: 'Info Required' },
  active:        { ar: 'نشط',             en: 'Active' },
  trial:         { ar: 'تجريبي',          en: 'Trial' },
  suspended:     { ar: 'موقوف',           en: 'Suspended' },
  open:          { ar: 'مفتوح',           en: 'Open' },
  in_progress:   { ar: 'قيد المعالجة',   en: 'In Progress' },
  resolved:      { ar: 'محلول',           en: 'Resolved' },
  closed:        { ar: 'مغلق',            en: 'Closed' },
};

const priorityColors: Record<string, string> = {
  low: 'badge-neutral', medium: 'badge-warning', high: 'badge-danger', urgent: 'badge-danger',
};

const priorityLabels: Record<string, { ar: string; en: string }> = {
  low:    { ar: 'منخفضة', en: 'Low' },
  medium: { ar: 'متوسطة', en: 'Medium' },
  high:   { ar: 'عالية',  en: 'High' },
  urgent: { ar: 'عاجلة',  en: 'Urgent' },
};

// ── Real-time metrics interfaces ──────────────────────────────────────────────
interface CountryTaxMetric {
  country: string;
  country_code: string;
  tax_collected: number;
  invoice_count: number;
}

interface RealtimeMetrics {
  totalRevenue: number;
  taxByCountry: CountryTaxMetric[];
  zatcaRejectionRate: number;
  zatcaTotal: number;
  zatcaRejected: number;
  pendingApprovals: number;
  offlineQueuePending: number;
  offlineQueueFailed: number;
  offlineQueueSynced: number;
  lastUpdated: Date | null;
}

function MiniBarChart({ data, color = '#0F5C5C' }: { data: number[]; color?: string }) {
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end gap-0.5 h-8">
      {data.map((v, i) => (
        <div
          key={i}
          style={{ height: `${(v / max) * 100}%`, backgroundColor: color, opacity: i === data.length - 1 ? 1 : 0.4 }}
          className="flex-1 rounded-sm min-h-[2px]"
        />
      ))}
    </div>
  );
}

function StatCard({
  label, value, sub, icon: Icon, color, bg, border, trend, trendVal, sparkData,
}: {
  label: string; value: string | number; sub?: string;
  icon: React.ElementType; color: string; bg: string; border: string;
  trend?: 'up' | 'down' | 'neutral'; trendVal?: string; sparkData?: number[];
}) {
  return (
    <div className={`bg-white border ${border} rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}>
          <Icon size={18} className={color} />
        </div>
        {trend && trendVal && (
          <div className={`flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${
            trend === 'up' ? 'bg-green-50 text-green-700' : trend === 'down' ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-600'
          }`}>
            {trend === 'up' ? <ArrowUpRight size={11} /> : trend === 'down' ? <ArrowDownRight size={11} /> : null}
            {trendVal}
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-[#0D1117]">{value}</p>
      <p className="text-sm text-[#6B7280] mt-0.5">{label}</p>
      {sub && <p className="text-xs text-[#9CA3AF] mt-0.5">{sub}</p>}
      {sparkData && (
        <div className="mt-3">
          <MiniBarChart data={sparkData} color={color.includes('#') ? color.replace('text-[', '').replace(']', '') : '#0F5C5C'} />
        </div>
      )}
    </div>
  );
}

function HealthItem({ label, status, value, icon: Icon }: { label: string; status: 'ok' | 'warn' | 'error'; value: string; icon: React.ElementType }) {
  const colors = { ok: 'text-green-600 bg-green-50', warn: 'text-amber-600 bg-amber-50', error: 'text-red-600 bg-red-50' };
  const dots = { ok: 'bg-green-500', warn: 'bg-amber-500', error: 'bg-red-500' };
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#F0F2F5] last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg ${colors[status]} flex items-center justify-center`}>
          <Icon size={14} />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0D1117]">{label}</p>
          <p className="text-xs text-[#6B7280]">{value}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <div className={`w-2 h-2 rounded-full ${dots[status]} animate-pulse`} />
        <span className={`text-xs font-bold ${status === 'ok' ? 'text-green-700' : status === 'warn' ? 'text-amber-700' : 'text-red-700'}`}>
          {status === 'ok' ? 'يعمل' : status === 'warn' ? 'تحذير' : 'خطأ'}
        </span>
      </div>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const { profile, signOut } = useAuth();
  const supabase = createClient();
  const { lang } = useTranslation();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [joinRequests, setJoinRequests] = useState<JoinRequest[]>([]);
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [contactMessages, setContactMessages] = useState<ContactMessage[]>([]);
  const [selectedContactMessage, setSelectedContactMessage] = useState<ContactMessage | null>(null);
  const [contactMessageNotes, setContactMessageNotes] = useState('');
  const [contactMessageUpdating, setContactMessageUpdating] = useState(false);
  const [packages, setPackages] = useState<Package[]>(defaultPackages);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<JoinRequest | null>(null);
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [packageModal, setPackageModal] = useState(false);
  const [editPackage, setEditPackage] = useState<Package | null>(null);
  const [pkgForm, setPkgForm] = useState<Partial<Package>>({});
  const [pkgSaving, setPkgSaving] = useState(false);
  const [companyFilter, setCompanyFilter] = useState<'all' | 'active' | 'suspended' | 'trial'>('all');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyDetailModal, setCompanyDetailModal] = useState(false);
  const [notifModal, setNotifModal] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [notifSending, setNotifSending] = useState(false);
  const [editCompanyModal, setEditCompanyModal] = useState(false);
  const [editCompanyForm, setEditCompanyForm] = useState<Partial<Company>>({});
  const [editCompanySaving, setEditCompanySaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [resetCompanyAdminLoading, setResetCompanyAdminLoading] = useState<string | null>(null);
  // Company password change state
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [changePasswordCompany, setChangePasswordCompany] = useState<Company | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // ── Real-time metrics state ────────────────────────────────────────────────
  const [realtimeMetrics, setRealtimeMetrics] = useState<RealtimeMetrics>({
    totalRevenue: 0,
    taxByCountry: [],
    zatcaRejectionRate: 0,
    zatcaTotal: 0,
    zatcaRejected: 0,
    pendingApprovals: 0,
    offlineQueuePending: 0,
    offlineQueueFailed: 0,
    offlineQueueSynced: 0,
    lastUpdated: null,
  });
  const [metricsLoading, setMetricsLoading] = useState(false);

  // ── Revenue metrics state (MRR / ARR / ARPU / package breakdown) ──────────
  const [revenueMetrics, setRevenueMetrics] = useState<{
    mrr: number;
    arr: number;
    arpu: number;
    packageBreakdown: { packageId: string; packageName: string; count: number; monthlyRevenue: number }[];
  }>({ mrr: 0, arr: 0, arpu: 0, packageBreakdown: [] });

  const fetchRealtimeMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      // Fetch invoices for revenue + tax by country + ZATCA stats
      const [invoicesRes, approvalsRes, syncQueueRes] = await Promise.all([
        supabase.from('invoices').select('total_amount, tax_amount, country_code, zatca_status, currency').limit(1000),
        supabase.from('join_requests').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('transaction_sync_queue').select('status', { count: 'exact' }),
      ]);

      // ── Revenue & Tax by country ──────────────────────────────────────────
      let totalRevenue = 0;
      const taxMap: Record<string, CountryTaxMetric> = {};
      let zatcaTotal = 0;
      let zatcaRejected = 0;

      if (invoicesRes.data) {
        for (const inv of invoicesRes.data) {
          const amount = Number(inv.total_amount) || 0;
          const tax = Number(inv.tax_amount) || 0;
          totalRevenue += amount;

          const cc = (inv.country_code as string) || 'SA';
          const countryNames: Record<string, string> = {
            SA: lang === 'ar' ? 'السعودية' : 'Saudi Arabia',
            AE: lang === 'ar' ? 'الإمارات' : 'UAE',
            KW: lang === 'ar' ? 'الكويت' : 'Kuwait',
            BH: lang === 'ar' ? 'البحرين' : 'Bahrain',
            QA: lang === 'ar' ? 'قطر' : 'Qatar',
            OM: lang === 'ar' ? 'عُمان' : 'Oman',
            EG: lang === 'ar' ? 'مصر' : 'Egypt',
            JO: lang === 'ar' ? 'الأردن' : 'Jordan',
          };
          if (!taxMap[cc]) {
            taxMap[cc] = { country: countryNames[cc] || cc, country_code: cc, tax_collected: 0, invoice_count: 0 };
          }
          taxMap[cc].tax_collected += tax;
          taxMap[cc].invoice_count += 1;

          // ZATCA stats
          if (inv.zatca_status) {
            zatcaTotal += 1;
            if (inv.zatca_status === 'rejected' || inv.zatca_status === 'error') zatcaRejected += 1;
          }
        }
      }

      const taxByCountry = Object.values(taxMap).sort((a, b) => b.tax_collected - a.tax_collected);
      const zatcaRejectionRate = zatcaTotal > 0 ? Math.round((zatcaRejected / zatcaTotal) * 100) : 0;

      // ── Pending approvals ─────────────────────────────────────────────────
      const pendingApprovals = approvalsRes.count ?? 0;

      // ── Offline sync queue ────────────────────────────────────────────────
      let offlineQueuePending = 0;
      let offlineQueueFailed = 0;
      let offlineQueueSynced = 0;
      if (syncQueueRes.data) {
        for (const row of syncQueueRes.data) {
          if (row.status === 'pending') offlineQueuePending += 1;
          else if (row.status === 'failed') offlineQueueFailed += 1;
          else if (row.status === 'synced' || row.status === 'completed') offlineQueueSynced += 1;
        }
      }

      setRealtimeMetrics({
        totalRevenue,
        taxByCountry: taxByCountry.length > 0 ? taxByCountry : [],
        zatcaRejectionRate,
        zatcaTotal,
        zatcaRejected,
        pendingApprovals,
        offlineQueuePending,
        offlineQueueFailed,
        offlineQueueSynced,
        lastUpdated: new Date(),
      });
    } catch {
      // Silently fail — metrics are supplementary
    } finally {
      setMetricsLoading(false);
    }
  }, [supabase, lang]);

  const fetchRevenueMetrics = useCallback(async () => {
    try {
      // Fetch active subscriptions joined with package prices
      const { data: subs } = await supabase
        .from('company_subscriptions')
        .select('id, company_id, package_id, billing_cycle, amount, status, subscription_packages(id, name_ar, name_en, price_monthly, price_yearly)')
        .in('status', ['active', 'trial']);

      if (!subs || subs.length === 0) {
        setRevenueMetrics({ mrr: 0, arr: 0, arpu: 0, packageBreakdown: [] });
        return;
      }

      let totalMRR = 0;
      const pkgMap: Record<string, { packageId: string; packageName: string; count: number; monthlyRevenue: number }> = {};

      for (const sub of subs) {
        const pkg = sub.subscription_packages as { id: string; name_ar: string; name_en: string; price_monthly: number; price_yearly: number } | null;
        let monthlyAmount = 0;

        if (sub.amount && Number(sub.amount) > 0) {
          // Use the stored amount, normalise to monthly
          monthlyAmount = sub.billing_cycle === 'yearly'
            ? Number(sub.amount) / 12
            : Number(sub.amount);
        } else if (pkg) {
          monthlyAmount = sub.billing_cycle === 'yearly'
            ? (pkg.price_yearly / 12)
            : pkg.price_monthly;
        }

        totalMRR += monthlyAmount;

        const pkgId = pkg?.id ?? 'unknown';
        const pkgName = pkg ? (lang === 'ar' ? pkg.name_ar : pkg.name_en) : 'Unknown';
        if (!pkgMap[pkgId]) {
          pkgMap[pkgId] = { packageId: pkgId, packageName: pkgName, count: 0, monthlyRevenue: 0 };
        }
        pkgMap[pkgId].count += 1;
        pkgMap[pkgId].monthlyRevenue += monthlyAmount;
      }

      const activeSubCount = subs.length;
      const arpu = activeSubCount > 0 ? Math.round(totalMRR / activeSubCount) : 0;

      setRevenueMetrics({
        mrr: Math.round(totalMRR),
        arr: Math.round(totalMRR * 12),
        arpu,
        packageBreakdown: Object.values(pkgMap).sort((a, b) => b.monthlyRevenue - a.monthlyRevenue),
      });
    } catch {
      // Silently fail — show zeros
      setRevenueMetrics({ mrr: 0, arr: 0, arpu: 0, packageBreakdown: [] });
    }
  }, [supabase, lang]);

  // Poll every 30 seconds
  useEffect(() => {
    fetchRealtimeMetrics();
    const interval = setInterval(fetchRealtimeMetrics, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchRevenueMetrics(); }, [fetchRevenueMetrics]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [companiesRes, requestsRes, complaintsRes, packagesRes, contactRes] = await Promise.all([
        supabase.from('companies').select('*').order('created_at', { ascending: false }),
        supabase.from('join_requests').select('*').in('status', ['pending', 'info_required']).order('created_at', { ascending: false }),
        supabase.from('complaints').select('*').order('created_at', { ascending: false }),
        supabase.from('subscription_packages').select('*').order('sort_order'),
        supabase.from('contact_us_messages').select('*').order('created_at', { ascending: false }),
      ]);
      if (companiesRes.data) setCompanies(companiesRes.data as Company[]);
      if (requestsRes.data) setJoinRequests(requestsRes.data as JoinRequest[]);
      if (complaintsRes.data) setComplaints(complaintsRes.data as Complaint[]);
      if (packagesRes.data && packagesRes.data.length > 0) setPackages(packagesRes.data as Package[]);
      if (contactRes.data) setContactMessages(contactRes.data as ContactMessage[]);
    } catch {
      toast.error(lang === 'ar' ? 'خطأ في تحميل البيانات' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sl = (key: string) => statusLabels[key]?.[lang] ?? key;
  const pl = (key: string) => priorityLabels[key]?.[lang] ?? key;

  const handleReviewRequest = async (id: string, status: 'approved' | 'rejected' | 'info_required') => {
    setReviewLoading(true);
    try {
      if (status === 'rejected') {
        // Delete the rejected request entirely from DB and state
        await supabase.from('join_requests').delete().eq('id', id);
        setJoinRequests(prev => prev.filter(r => r.id !== id));
        setSelectedRequest(null);
        setReviewNotes('');
        toast.success(lang === 'ar' ? 'تم رفض وحذف الطلب نهائياً' : 'Request rejected and deleted');
      } else if (status === 'approved') {
        const req = joinRequests.find(r => r.id === id);
        if (!req) {
          toast.error(lang === 'ar' ? 'لم يتم العثور على الطلب' : 'Request not found');
          return;
        }

        // Call the Edge Function which uses service role to create auth user + company + profile
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;

        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
        const edgeFnUrl = `${supabaseUrl}/functions/v1/approve-join-request`;

        const response = await fetch(edgeFnUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'apikey': process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ?? '',
          },
          body: JSON.stringify({
            joinRequestId: id,
            reviewerNotes: reviewNotes,
          }),
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to approve request');
        }

        // Remove from join requests state immediately
        setJoinRequests(prev => prev.filter(r => r.id !== id));

        // Fetch the newly created company from DB and add to companies state
        if (result.companyId) {
          const { data: newCompany } = await supabase
            .from('companies')
            .select('*')
            .eq('id', result.companyId)
            .maybeSingle();
          if (newCompany) {
            setCompanies(prev => {
              // Avoid duplicates
              if (prev.some(c => c.id === newCompany.id)) return prev;
              return [...prev, newCompany as Company];
            });
          }
        } else if (result.company) {
          setCompanies(prev => {
            if (prev.some(c => c.id === (result.company as Company).id)) return prev;
            return [...prev, result.company as Company];
          });
        }

        setSelectedRequest(null);
        setReviewNotes('');
        toast.success(
          lang === 'ar' ?'✅ تم قبول الطلب وإنشاء حساب الشركة والمدير — يمكنه تسجيل الدخول الآن' :'✅ Request approved — company and admin account created, they can log in now'
        );
      } else {
        // info_required: update status and send email notification
        const req = joinRequests.find(r => r.id === id);
        await supabase.from('join_requests').update({ status, reviewer_notes: reviewNotes, reviewed_at: new Date().toISOString() }).eq('id', id);
        setJoinRequests(prev => prev.map(r => r.id === id ? { ...r, status, reviewer_notes: reviewNotes } : r));

        // Send email notification to the owner
        if (req?.admin_email) {
          try {
            const emailBody = `mailto:${req.admin_email}?subject=${encodeURIComponent('طلب معلومات إضافية - Nexora')}&body=${encodeURIComponent(
              `عزيزي ${req.admin_name || 'صاحب الطلب'},\n\nشكراً لتقديم طلب انضمام شركة "${req.company_name_ar}" إلى منصة Nexora.\n\nبعد مراجعة طلبكم، نحتاج إلى معلومات أو مستندات إضافية:\n\n${reviewNotes || 'يرجى التواصل معنا لمزيد من التفاصيل.'}\n\nيرجى الرد على هذا البريد أو التواصل معنا في أقرب وقت ممكن.\n\nمع تحيات فريق Nexora`
            )}`;
            window.open(emailBody, '_blank');
          } catch {
            // Email client may not be available, silently ignore
          }
        }

        setSelectedRequest(null);
        setReviewNotes('');
        toast.success(lang === 'ar' ? 'تم طلب معلومات إضافية وإرسال إشعار للمالك' : 'Additional info requested and owner notified');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      toast.error(
        lang === 'ar'
          ? `خطأ في تحديث الطلب: ${message || 'حدث خطأ غير متوقع'}`
          : `Error updating request: ${message || 'Unexpected error'}`
      );
    } finally {
      setReviewLoading(false);
    }
  };

  const handleResolveComplaint = async (id: string, status: 'resolved' | 'in_progress' | 'closed') => {
    try {
      await supabase.from('complaints').update({ status }).eq('id', id);
      setComplaints(prev => prev.map(c => c.id === id ? { ...c, status } : c));
      setSelectedComplaint(null);
      toast.success('تم تحديث حالة الشكوى');
    } catch {
      toast.error('خطأ في تحديث الشكوى');
    }
  };

  const handleToggleCompany = async (company: Company) => {
    try {
      const newActive = !company.is_active;
      await supabase.from('companies').update({
        is_active: newActive,
        subscription_status: newActive ? 'active' : 'suspended',
        is_read_only: !newActive,
        read_only_reason: !newActive ? 'manually_suspended' : null,
      }).eq('id', company.id);
      setCompanies(prev => prev.map(c => c.id === company.id ? {
        ...c,
        is_active: newActive,
        subscription_status: newActive ? 'active' : 'suspended',
        is_read_only: !newActive,
      } : c));
      toast.success(company.is_active
        ? (lang === 'ar' ? 'تم تعليق الشركة — وضع القراءة فقط' : 'Company suspended — read-only mode')
        : (lang === 'ar' ? 'تم تفعيل الشركة بنجاح' : 'Company reactivated successfully'));
    } catch {
      toast.error(lang === 'ar' ? 'خطأ في تحديث حالة الشركة' : 'Error updating company status');
    }
  };

  const handleReactivateCompany = async (company: Company) => {
    try {
      const { data, error } = await supabase.rpc('platform_owner_reactivate_company', {
        p_company_id: company.id,
        p_new_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error);
      setCompanies(prev => prev.map(c => c.id === company.id ? {
        ...c,
        is_active: true,
        subscription_status: 'active',
        is_read_only: false,
        read_only_reason: null,
      } : c));
      toast.success(lang === 'ar' ? '✅ تم إعادة تفعيل الشركة بنجاح' : '✅ Company reactivated successfully');
      setCompanyDetailModal(false);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : (lang === 'ar' ? 'خطأ في إعادة التفعيل' : 'Reactivation error'));
    }
  };

  const handleUpdateCompanyLimits = async (companyId: string, maxUsers: number, maxBranches: number) => {
    try {
      const { data, error } = await supabase.rpc('platform_owner_update_limits', {
        p_company_id: companyId,
        p_max_users: maxUsers,
        p_max_branches: maxBranches,
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error);
      setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, max_users: maxUsers, max_branches: maxBranches } : c));
      toast.success(lang === 'ar' ? '✅ تم تحديث حدود الشركة' : '✅ Company limits updated');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : (lang === 'ar' ? 'خطأ في تحديث الحدود' : 'Error updating limits'));
    }
  };

  const handleSavePackage = async () => {
    if (!pkgForm.name_ar || pkgForm.price_monthly === undefined) {
      toast.error('اسم الباقة والسعر مطلوبان');
      return;
    }
    setPkgSaving(true);
    try {
      if (editPackage) {
        await supabase.from('subscription_packages').update(pkgForm).eq('id', editPackage.id);
        setPackages(prev => prev.map(p => p.id === editPackage.id ? { ...p, ...pkgForm } as Package : p));
        toast.success('تم تحديث الباقة');
      } else {
        const newPkg: Package = { ...pkgForm as Package, id: `pkg-${Date.now()}`, sort_order: packages.length + 1 };
        await supabase.from('subscription_packages').insert([pkgForm]);
        setPackages(prev => [...prev, newPkg]);
        toast.success('تم إضافة الباقة');
      }
      setPackageModal(false);
      setEditPackage(null);
    } catch {
      toast.error('خطأ في حفظ الباقة');
    } finally {
      setPkgSaving(false);
    }
  };

  const handleSendNotification = async () => {
    if (!notifMessage.trim()) return;
    setNotifSending(true);
    await new Promise(r => setTimeout(r, 1000));
    setNotifSending(false);
    setNotifModal(false);
    setNotifMessage('');
    toast.success('تم إرسال الإشعار لجميع الشركات');
  };

  const handleEditCompany = async () => {
    if (!editCompanyForm.id) return;
    setEditCompanySaving(true);
    try {
      const { id, ...updateData } = editCompanyForm;
      await supabase.from('companies').update(updateData).eq('id', id);
      setCompanies(prev => prev.map(c => c.id === id ? { ...c, ...updateData } as Company : c));
      setEditCompanyModal(false);
      setEditCompanyForm({});
      toast.success('تم تحديث بيانات الشركة');
    } catch {
      toast.error('خطأ في تحديث بيانات الشركة');
    } finally {
      setEditCompanySaving(false);
    }
  };

  const handleDeleteCompany = async (companyId: string) => {
    try {
      await supabase.from('companies').delete().eq('id', companyId);
      setCompanies(prev => prev.filter(c => c.id !== companyId));
      setDeleteConfirmId(null);
      setCompanyDetailModal(false);
      setSelectedCompany(null);
      toast.success('تم حذف الشركة نهائياً');
    } catch {
      toast.error('خطأ في حذف الشركة');
    }
  };

  const handleUpdateContactMessage = async (id: string, status: string) => {
    setContactMessageUpdating(true);
    try {
      await supabase.from('contact_us_messages').update({ status, admin_notes: contactMessageNotes, updated_at: new Date().toISOString() }).eq('id', id);
      setContactMessages(prev => prev.map(m => m.id === id ? { ...m, status, admin_notes: contactMessageNotes } : m));
      setSelectedContactMessage(null);
      setContactMessageNotes('');
      toast.success(lang === 'ar' ? 'تم تحديث الرسالة' : 'Message updated');
    } catch {
      toast.error(lang === 'ar' ? 'خطأ في تحديث الرسالة' : 'Error updating message');
    } finally {
      setContactMessageUpdating(false);
    }
  };

  const handleResetCompanyAdminPassword = async (company: Company) => {
    if (!company.email) {
      toast.error(lang === 'ar' ? 'لا يوجد بريد إلكتروني لهذه الشركة' : 'No email found for this company');
      return;
    }
    setResetCompanyAdminLoading(company.id);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(company.email, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback?next=/reset-password`,
      });
      if (error) {
        if (error.message.includes('rate limit') || error.message.includes('Too many')) {
          toast.error(lang === 'ar' ? 'تم إرسال بريد مؤخراً. يرجى الانتظار دقيقة.' : 'Email sent recently. Please wait a minute.');
        } else {
          toast.error(lang === 'ar' ? 'خطأ في إرسال رابط إعادة التعيين' : 'Error sending reset link');
        }
      } else {
        toast.success(lang === 'ar' ? `تم إرسال رابط إعادة تعيين كلمة المرور إلى ${company.email}` : `Password reset link sent to ${company.email}`);
      }
    } catch {
      toast.error(lang === 'ar' ? 'خطأ في إرسال رابط إعادة التعيين' : 'Error sending reset link');
    } finally {
      setResetCompanyAdminLoading(null);
    }
  };

  const handleChangeCompanyPassword = async () => {
    if (!changePasswordCompany) return;
    if (newPassword.length < 8) {
      toast.error(lang === 'ar' ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' : 'Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(lang === 'ar' ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match');
      return;
    }
    setChangePasswordLoading(true);
    try {
      const { data, error } = await supabase.rpc('platform_owner_set_company_password', {
        p_company_id: changePasswordCompany.id,
        p_new_password: newPassword,
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error);
      toast.success(
        lang === 'ar'
          ? `✅ تم تغيير كلمة مرور مدير الشركة بنجاح (${data?.user_email || changePasswordCompany.email})`
          : `✅ Company admin password changed successfully (${data?.user_email || changePasswordCompany.email})`
      );
      setChangePasswordModal(false);
      setChangePasswordCompany(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : (lang === 'ar' ? 'خطأ في تغيير كلمة المرور' : 'Error changing password'));
    } finally {
      setChangePasswordLoading(false);
    }
  };

  // ── Derived stats ──────────────────────────────────────────────────────────
  const pendingRequests = joinRequests.filter(r => r.status === 'pending' || r.status === 'info_required').length;
  const openComplaints = complaints.filter(c => c.status === 'open').length;
  const newContactMessages = contactMessages.filter(m => m.status === 'new').length;
  const activeCompanies = companies.filter(c => c.is_active).length;
  const suspendedCompanies = companies.filter(c => !c.is_active).length;
  const trialCompanies = companies.filter(c => c.subscription_status === 'trial').length;
  const approvedRequests = joinRequests.filter(r => r.status === 'approved').length;
  const resolvedComplaints = complaints.filter(c => c.status === 'resolved').length;

  // Revenue estimates
  const estimatedMRR = revenueMetrics.mrr;
  const estimatedARR = revenueMetrics.arr;

  const filteredCompanies = companies.filter(c => {
    const matchSearch = !search || c.name_ar?.includes(search) || c.name_en?.toLowerCase().includes(search.toLowerCase()) || c.cr_number?.includes(search);
    const matchFilter = companyFilter === 'all' || (companyFilter === 'active' && c.is_active) || (companyFilter === 'suspended' && !c.is_active) || (companyFilter === 'trial' && c.subscription_status === 'trial');
    return matchSearch && matchFilter;
  });
  const filteredRequests = joinRequests.filter(r => {
    // Only show pending and info_required — approved move to companies, rejected are deleted
    if (r.status === 'approved' || r.status === 'rejected') return false;
    return !search || r.company_name_ar?.includes(search) || r.admin_email?.includes(search);
  });
  const filteredComplaints = complaints.filter(c =>
    !search || c.subject?.includes(search)
  );
  const filteredContactMessages = contactMessages.filter(m =>
    !search || m.name?.includes(search) || m.email?.toLowerCase().includes(search.toLowerCase()) || m.subject?.includes(search)
  );

  // Spark data
  const companySpark = [Math.max(1, activeCompanies - 5), Math.max(1, activeCompanies - 3), Math.max(1, activeCompanies - 2), Math.max(1, activeCompanies - 1), activeCompanies];
  const requestSpark = [2, 3, 1, pendingRequests + 1, pendingRequests];
  const complaintSpark = [1, 2, openComplaints + 2, openComplaints + 1, openComplaints];
  const mrrSpark = [estimatedMRR * 0.7, estimatedMRR * 0.8, estimatedMRR * 0.85, estimatedMRR * 0.95, estimatedMRR];

  const tabs: { id: AdminTab; label: { ar: string; en: string }; icon: React.ElementType; badge?: number }[] = [
    { id: 'overview',          label: { ar: 'نظرة عامة',       en: 'Overview' },         icon: BarChart2 },
    { id: 'revenue',           label: { ar: 'الإيرادات',        en: 'Revenue' },          icon: DollarSign },
    { id: 'analytics',         label: { ar: 'التحليلات',        en: 'Analytics' },        icon: TrendingUp },
    { id: 'companies',         label: { ar: 'الشركات',          en: 'Companies' },        icon: Building2,     badge: companies.length },
    { id: 'join_requests',     label: { ar: 'طلبات الانضمام',   en: 'Join Requests' },    icon: FileText,      badge: pendingRequests },
    { id: 'subscriptions',     label: { ar: 'الاشتراكات',       en: 'Subscriptions' },    icon: CreditCard },
    { id: 'complaints',        label: { ar: 'الشكاوى',          en: 'Complaints' },       icon: MessageSquare, badge: openComplaints },
    { id: 'contact_messages',  label: { ar: 'رسائل التواصل',    en: 'Contact Messages' }, icon: Phone,         badge: newContactMessages },
    { id: 'packages',          label: { ar: 'الباقات',          en: 'Packages' },         icon: Package },
    { id: 'platform_users',    label: { ar: 'مستخدمو المنصة',     en: 'Platform Users' },     icon: Users },
    { id: 'auth_admin',        label: { ar: 'إدارة المصادقة',   en: 'Auth Admin' },       icon: Shield },
    { id: 'auth_alerts',       label: { ar: 'تنبيهات الأمان',   en: 'Auth Alerts' },      icon: Bell },
    { id: 'admin_logs',        label: { ar: 'سجل الإجراءات',    en: 'Admin Logs' },       icon: Activity },
    { id: 'system',            label: { ar: 'صحة النظام',       en: 'System Health' },    icon: Server },
  ];

  return (
    <div className="min-h-screen bg-[#F7F8FA]" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* ── Top Bar ─────────────────────────────────────────────────────────── */}
      <header className="h-14 bg-white border-b border-[#E2E6EA] flex items-center px-6 gap-4 shadow-sm sticky top-0 z-20">
        {/* Back to main app button */}
        <button
          onClick={() => router.push('/')}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-[#EEF0F3] text-[#6B7280] hover:text-[#0D1117] transition-colors text-xs font-semibold shrink-0"
          title={lang === 'ar' ? 'العودة للتطبيق' : 'Back to App'}
        >
          {lang === 'ar' ? <ArrowLeft size={14} /> : <ArrowRight size={14} />}
          <Home size={13} />
          <span className="hidden sm:inline">{lang === 'ar' ? 'الرئيسية' : 'Home'}</span>
        </button>
        <div className="w-px h-5 bg-[#E2E6EA] shrink-0" />
        <div className="flex items-center gap-2.5">
          <NexoraMark size={32} />
          <div>
            <span className="font-bold text-[#0F5C5C] text-sm leading-none block">Nexora</span>
            <span className="text-[9px] text-[#6B7280] tracking-widest uppercase">Super Admin</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 ms-4 px-2.5 py-1 bg-purple-50 border border-purple-200 rounded-full">
          <Crown size={11} className="text-purple-600" />
          <span className="text-[10px] font-bold text-purple-700">{lang === 'ar' ? 'مدير المنصة' : 'Platform Owner'}</span>
        </div>
        <div className="hidden md:flex items-center gap-1.5 ms-2 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[10px] font-bold text-green-700">{lang === 'ar' ? 'النظام يعمل' : 'System Online'}</span>
        </div>
        <div className="ms-auto flex items-center gap-2">
          <button onClick={() => setNotifModal(true)} className="p-1.5 rounded-lg hover:bg-[#EEF0F3] text-[#6B7280] transition-colors relative" title={lang === 'ar' ? 'إرسال إشعار' : 'Send Notification'}>
            <Bell size={15} />
            {(pendingRequests + openComplaints + newContactMessages) > 0 && (
              <span className="absolute -top-0.5 -end-0.5 w-3.5 h-3.5 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                {pendingRequests + openComplaints + newContactMessages}
              </span>
            )}
          </button>
          <button onClick={fetchData} className="p-1.5 rounded-lg hover:bg-[#EEF0F3] text-[#6B7280] transition-colors" title={lang === 'ar' ? 'تحديث' : 'Refresh'}>
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#EEF0F3]">
            <div className="w-6 h-6 rounded-full bg-[#0F5C5C] flex items-center justify-center text-white text-xs font-bold">
              {profile?.full_name?.charAt(0) || 'م'}
            </div>
            <span className="text-xs font-bold text-[#0D1117]">{profile?.full_name || (lang === 'ar' ? 'المدير' : 'Admin')}</span>
          </div>
          <button onClick={() => signOut()} className="p-1.5 rounded-lg hover:bg-red-50 text-[#6B7280] hover:text-red-600 transition-colors" title={lang === 'ar' ? 'تسجيل الخروج' : 'Sign Out'}>
            <LogOut size={15} />
          </button>
        </div>
      </header>

      <div className="flex">
        {/* ── Sidebar ───────────────────────────────────────────────────────── */}
        <aside className="w-56 shrink-0 bg-white border-e border-[#E2E6EA] min-h-[calc(100vh-56px)] sticky top-14 p-3 flex flex-col">
          <nav className="space-y-0.5 flex-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSearch(''); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all text-start ${
                  activeTab === tab.id
                    ? 'bg-[#0F5C5C] text-white font-bold shadow-sm'
                    : 'text-[#6B7280] hover:bg-[#F0F2F5] hover:text-[#0D1117]'
                }`}
              >
                <tab.icon size={15} className="shrink-0" />
                <span className="flex-1 font-semibold">{tab.label[lang]}</span>
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className={`text-[9px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-[#0F5C5C] text-white'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
          <div className="mt-4 pt-4 border-t border-[#E2E6EA]">
            <div className="bg-[#F7F8FA] rounded-xl p-3">
              <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-2">{lang === 'ar' ? 'إحصائية سريعة' : 'Quick Stats'}</p>
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-[#6B7280]">{lang === 'ar' ? 'الشركات النشطة' : 'Active Companies'}</span>
                  <span className="font-bold text-green-700">{activeCompanies}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#6B7280]">{lang === 'ar' ? 'طلبات معلقة' : 'Pending Requests'}</span>
                  <span className="font-bold text-amber-700">{pendingRequests}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#6B7280]">{lang === 'ar' ? 'شكاوى مفتوحة' : 'Open Complaints'}</span>
                  <span className="font-bold text-red-700">{openComplaints}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#6B7280]">{lang === 'ar' ? 'رسائل جديدة' : 'New Messages'}</span>
                  <span className="font-bold text-blue-700">{newContactMessages}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#6B7280]">{lang === 'ar' ? 'MRR' : 'MRR'}</span>
                  <span className="font-bold text-[#0F5C5C]">{estimatedMRR > 0 ? `${(estimatedMRR / 1000).toFixed(1)}K` : '0'}</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* ── Main Content ──────────────────────────────────────────────────── */}
        <main className="flex-1 p-6 min-w-0">
          {/* Search bar for filterable tabs */}
          {!['overview', 'packages', 'analytics', 'system', 'revenue', 'auth_admin'].includes(activeTab) && (
            <div className="relative mb-5 max-w-sm">
              <Search size={14} className="absolute top-1/2 -translate-y-1/2 end-3 text-[#6B7280]" />
              <input
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder={lang === 'ar' ? 'بحث...' : 'Search...'}
                className="w-full h-10 bg-white border border-[#E2E6EA] rounded-xl pe-9 ps-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C]"
              />
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              OVERVIEW TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h1 className="text-xl font-bold text-[#0D1117]">{lang === 'ar' ? 'لوحة مدير المنصة' : 'Platform Admin Dashboard'}</h1>
                  <p className="text-sm text-[#6B7280] mt-0.5">Nexora ERP — {lang === 'ar' ? 'نظرة شاملة على المنصة' : 'Platform Overview'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setNotifModal(true)} className="flex items-center gap-1.5 h-9 px-4 bg-purple-600 text-white rounded-xl text-sm font-bold hover:bg-purple-700 transition-colors">
                    <Bell size={14} />{lang === 'ar' ? 'إشعار عام' : 'Broadcast'}
                  </button>
                  <button onClick={fetchData} className="flex items-center gap-1.5 h-9 px-4 bg-[#EAF4F4] text-[#0F5C5C] rounded-xl text-sm font-bold hover:bg-[#0F5C5C] hover:text-white transition-colors">
                    <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />{lang === 'ar' ? 'تحديث' : 'Refresh'}
                  </button>
                </div>
              </div>

              {/* ── Real-Time Metrics Section ─────────────────────────────── */}
              <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-[#E2E6EA] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <h3 className="font-bold text-sm text-[#0D1117]">{lang === 'ar' ? 'المقاييس الفورية' : 'Real-Time Metrics'}</h3>
                    {metricsLoading && <Loader2 size={13} className="animate-spin text-[#6B7280]" />}
                  </div>
                  <div className="flex items-center gap-3">
                    {realtimeMetrics.lastUpdated && (
                      <span className="text-[10px] text-[#9CA3AF]">
                        {lang === 'ar' ? 'آخر تحديث: ' : 'Updated: '}
                        {realtimeMetrics.lastUpdated.toLocaleTimeString(lang === 'ar' ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </span>
                    )}
                    <button onClick={fetchRealtimeMetrics} disabled={metricsLoading} className="flex items-center gap-1 text-xs text-[#0F5C5C] font-semibold hover:underline disabled:opacity-50">
                      <RefreshCw size={11} className={metricsLoading ? 'animate-spin' : ''} />
                      {lang === 'ar' ? 'تحديث' : 'Refresh'}
                    </button>
                  </div>
                </div>

                <div className="p-5 space-y-5">
                  {/* Row 1: Revenue + Pending Approvals + ZATCA + Offline Queue */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Total Revenue */}
                    <div className="bg-gradient-to-br from-[#0F5C5C] to-[#0A3D3D] rounded-xl p-4 text-white">
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                          <DollarSign size={15} />
                        </div>
                        <span className="text-[9px] font-bold bg-white/20 px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                          {lang === 'ar' ? 'مباشر' : 'Live'}
                        </span>
                      </div>
                      <p className="text-xl font-bold mt-1">
                        {realtimeMetrics.totalRevenue > 0
                          ? `${(realtimeMetrics.totalRevenue / 1000).toFixed(1)}K`
                          : `${(estimatedMRR / 1000).toFixed(0)}K`}
                      </p>
                      <p className="text-[11px] text-white/70 mt-0.5">{lang === 'ar' ? 'إجمالي الإيرادات (ر.س)' : 'Total Revenue (SAR)'}</p>
                      {realtimeMetrics.totalRevenue === 0 && estimatedMRR === 0 && (
                        <p className="text-[10px] text-white/50 mt-2">{lang === 'ar' ? 'لا توجد بيانات' : 'No data yet'}</p>
                      )}
                    </div>

                    {/* Pending Approvals */}
                    <div className={`rounded-xl p-4 border ${realtimeMetrics.pendingApprovals > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${realtimeMetrics.pendingApprovals > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
                          <CheckSquare size={15} className={realtimeMetrics.pendingApprovals > 0 ? 'text-amber-700' : 'text-green-700'} />
                        </div>
                        {realtimeMetrics.pendingApprovals > 0 && (
                          <span className="text-[9px] font-bold bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full">
                            {lang === 'ar' ? 'يحتاج مراجعة' : 'Needs Review'}
                          </span>
                        )}
                      </div>
                      <p className={`text-xl font-bold mt-1 ${realtimeMetrics.pendingApprovals > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                        {realtimeMetrics.pendingApprovals > 0 ? realtimeMetrics.pendingApprovals : pendingRequests}
                      </p>
                      <p className="text-[11px] text-[#6B7280] mt-0.5">{lang === 'ar' ? 'موافقات معلقة' : 'Pending Approvals'}</p>
                      <button onClick={() => setActiveTab('join_requests')} className={`mt-2 text-[10px] font-semibold hover:underline ${realtimeMetrics.pendingApprovals > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                        {lang === 'ar' ? 'مراجعة الطلبات ←' : 'Review Requests →'}
                      </button>
                    </div>

                    {/* ZATCA Rejection Rate */}
                    <div className={`rounded-xl p-4 border ${realtimeMetrics.zatcaRejectionRate > 5 ? 'bg-red-50 border-red-200' : realtimeMetrics.zatcaRejectionRate > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${realtimeMetrics.zatcaRejectionRate > 5 ? 'bg-red-100' : realtimeMetrics.zatcaRejectionRate > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
                          {realtimeMetrics.zatcaRejectionRate > 5
                            ? <ShieldAlert size={15} className="text-red-700" />
                            : <ShieldCheck size={15} className={realtimeMetrics.zatcaRejectionRate > 0 ? 'text-amber-700' : 'text-green-700'} />}
                        </div>
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${realtimeMetrics.zatcaRejectionRate > 5 ? 'bg-red-200 text-red-800' : realtimeMetrics.zatcaRejectionRate > 0 ? 'bg-amber-200 text-amber-800' : 'bg-green-200 text-green-800'}`}>
                          ZATCA
                        </span>
                      </div>
                      <p className={`text-xl font-bold mt-1 ${realtimeMetrics.zatcaRejectionRate > 5 ? 'text-red-800' : realtimeMetrics.zatcaRejectionRate > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                        {realtimeMetrics.zatcaRejectionRate}%
                      </p>
                      <p className="text-[11px] text-[#6B7280] mt-0.5">{lang === 'ar' ? 'معدل رفض زاتكا' : 'ZATCA Rejection Rate'}</p>
                      <p className="text-[10px] text-[#9CA3AF] mt-1">
                        {realtimeMetrics.zatcaTotal > 0
                          ? `${realtimeMetrics.zatcaRejected} / ${realtimeMetrics.zatcaTotal} ${lang === 'ar' ? 'فاتورة' : 'invoices'}`
                          : lang === 'ar' ? 'لا توجد بيانات بعد' : 'No data yet'}
                      </p>
                    </div>

                    {/* Offline Sync Queue */}
                    <div className={`rounded-xl p-4 border ${realtimeMetrics.offlineQueueFailed > 0 ? 'bg-red-50 border-red-200' : realtimeMetrics.offlineQueuePending > 0 ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${realtimeMetrics.offlineQueueFailed > 0 ? 'bg-red-100' : realtimeMetrics.offlineQueuePending > 0 ? 'bg-blue-100' : 'bg-green-100'}`}>
                          <CloudOff size={15} className={realtimeMetrics.offlineQueueFailed > 0 ? 'text-red-700' : realtimeMetrics.offlineQueuePending > 0 ? 'text-blue-700' : 'text-green-700'} />
                        </div>
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${realtimeMetrics.offlineQueueFailed > 0 ? 'bg-red-200 text-red-800' : realtimeMetrics.offlineQueuePending > 0 ? 'bg-blue-200 text-blue-800' : 'bg-green-200 text-green-800'}`}>
                          {lang === 'ar' ? 'مزامنة' : 'Sync'}
                        </span>
                      </div>
                      <p className={`text-xl font-bold mt-1 ${realtimeMetrics.offlineQueueFailed > 0 ? 'text-red-800' : realtimeMetrics.offlineQueuePending > 0 ? 'text-blue-800' : 'text-green-800'}`}>
                        {realtimeMetrics.offlineQueuePending + realtimeMetrics.offlineQueueFailed}
                      </p>
                      <p className="text-[11px] text-[#6B7280] mt-0.5">{lang === 'ar' ? 'قائمة المزامنة المعلقة' : 'Offline Sync Queue'}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        {realtimeMetrics.offlineQueuePending > 0 && (
                          <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold">
                            {realtimeMetrics.offlineQueuePending} {lang === 'ar' ? 'معلق' : 'pending'}
                          </span>
                        )}
                        {realtimeMetrics.offlineQueueFailed > 0 && (
                          <span className="text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-semibold">
                            {realtimeMetrics.offlineQueueFailed} {lang === 'ar' ? 'فشل' : 'failed'}
                          </span>
                        )}
                        {realtimeMetrics.offlineQueuePending === 0 && realtimeMetrics.offlineQueueFailed === 0 && (
                          <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">
                            {lang === 'ar' ? 'مزامنة كاملة' : 'Fully synced'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Tax Collected by Country */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin size={13} className="text-[#0F5C5C]" />
                      <h4 className="text-sm font-bold text-[#0D1117]">{lang === 'ar' ? 'الضريبة المحصلة حسب الدولة' : 'Tax Collected by Country'}</h4>
                    </div>
                    {realtimeMetrics.taxByCountry.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {realtimeMetrics.taxByCountry.slice(0, 8).map(item => {
                          const maxTax = Math.max(...realtimeMetrics.taxByCountry.map(t => t.tax_collected), 1);
                          const pct = Math.round((item.tax_collected / maxTax) * 100);
                          return (
                            <div key={item.country_code} className="bg-[#F7F8FA] border border-[#E2E6EA] rounded-xl p-3">
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-bold text-[#0D1117]">{item.country_code}</span>
                                  <span className="text-[10px] text-[#6B7280] truncate max-w-[80px]">{item.country}</span>
                                </div>
                                <Percent size={10} className="text-[#9CA3AF]" />
                              </div>
                              <p className="text-base font-bold text-[#0F5C5C]">
                                {item.tax_collected > 0
                                  ? item.tax_collected >= 1000
                                    ? `${(item.tax_collected / 1000).toFixed(1)}K`
                                    : item.tax_collected.toFixed(0)
                                  : '—'}
                              </p>
                              <p className="text-[10px] text-[#6B7280]">{lang === 'ar' ? 'ر.س ضريبة' : 'SAR tax'} · {item.invoice_count} {lang === 'ar' ? 'فاتورة' : 'inv'}</p>
                              <div className="mt-2 h-1 bg-[#E2E6EA] rounded-full overflow-hidden">
                                <div className="h-full bg-[#0F5C5C] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bg-[#F7F8FA] border border-[#E2E6EA] rounded-xl p-4 text-center">
                        <p className="text-sm text-[#6B7280]">{lang === 'ar' ? 'لا توجد بيانات ضريبية بعد' : 'No tax data available yet'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Primary KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label={lang === 'ar' ? 'الشركات النشطة' : 'Active Companies'} value={activeCompanies} sub={`${trialCompanies} ${lang === 'ar' ? 'تجريبي' : 'trial'}`} icon={Building2} color="text-green-700" bg="bg-green-50" border="border-green-200" trend="up" trendVal="" sparkData={companySpark} />
                <StatCard label={lang === 'ar' ? 'الإيرادات الشهرية' : 'Monthly Revenue'} value={`${(estimatedMRR / 1000).toFixed(0)}K ${lang === 'ar' ? 'ر.س' : 'SAR'}`} sub={`ARR: ${(estimatedARR / 1000).toFixed(0)}K`} icon={DollarSign} color="text-[#0F5C5C]" bg="bg-[#EAF4F4]" border="border-[#B2D8D8]" trend="up" trendVal="" sparkData={mrrSpark} />
                <StatCard label={lang === 'ar' ? 'طلبات معلقة' : 'Pending Requests'} value={pendingRequests} sub={`${approvedRequests} ${lang === 'ar' ? 'مقبول' : 'approved'}`} icon={FileText} color="text-amber-700" bg="bg-amber-50" border="border-amber-200" trend={pendingRequests > 3 ? 'up' : 'neutral'} trendVal={`${pendingRequests} ${lang === 'ar' ? 'جديد' : 'new'}`} sparkData={requestSpark} />
                <StatCard label={lang === 'ar' ? 'شكاوى مفتوحة' : 'Open Complaints'} value={openComplaints} sub={`${resolvedComplaints} ${lang === 'ar' ? 'محلول' : 'resolved'}`} icon={MessageSquare} color="text-red-700" bg="bg-red-50" border="border-red-200" trend={openComplaints > 2 ? 'up' : 'down'} trendVal={`${openComplaints} ${lang === 'ar' ? 'مفتوح' : 'open'}`} sparkData={complaintSpark} />
              </div>

              {/* Secondary stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: lang === 'ar' ? 'إجمالي الشركات' : 'Total Companies', value: companies.length, icon: Layers, color: 'text-blue-700', bg: 'bg-blue-50' },
                  { label: lang === 'ar' ? 'شركات موقوفة' : 'Suspended Companies', value: suspendedCompanies, icon: PauseCircle, color: 'text-red-600', bg: 'bg-red-50' },
                  { label: lang === 'ar' ? 'طلبات تحتاج معلومات' : 'Info Required', value: joinRequests.filter(r => r.status === 'info_required').length, icon: Info, color: 'text-blue-600', bg: 'bg-blue-50' },
                  { label: lang === 'ar' ? 'الباقات النشطة' : 'Active Packages', value: packages.filter(p => p.is_active).length, icon: Package, color: 'text-purple-700', bg: 'bg-purple-50' },
                ].map(s => (
                  <div key={s.label} className="bg-white border border-[#E2E6EA] rounded-xl p-4 flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl ${s.bg} flex items-center justify-center shrink-0`}>
                      <s.icon size={16} className={s.color} />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-[#0D1117]">{s.value}</p>
                      <p className="text-xs text-[#6B7280]">{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Two-column layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Recent join requests */}
                <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-[#E2E6EA] flex items-center justify-between">
                    <h3 className="font-bold text-sm text-[#0D1117]">{lang === 'ar' ? 'آخر طلبات الانضمام' : 'Recent Join Requests'}</h3>
                    <button onClick={() => setActiveTab('join_requests')} className="text-xs text-[#0F5C5C] font-semibold hover:underline flex items-center gap-1">
                      {lang === 'ar' ? 'عرض الكل' : 'View All'} <ChevronRight size={12} />
                    </button>
                  </div>
                  <div className="divide-y divide-[#F0F2F5]">
                    {filteredRequests.slice(0, 5).map(req => (
                      <div key={req.id} className="px-5 py-3 flex items-center justify-between hover:bg-[#FAFBFC] transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#EAF4F4] flex items-center justify-center text-[#0F5C5C] font-bold text-xs">
                            {req.company_name_ar?.charAt(0) || '؟'}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-[#0D1117]">{lang === 'ar' ? req.company_name_ar : (req.company_name_en || req.company_name_ar)}</p>
                            <p className="text-xs text-[#6B7280]">{req.admin_email}</p>
                          </div>
                        </div>
                        <span className={`swekt-badge text-xs ${statusColors[req.status] || 'badge-neutral'}`}>{sl(req.status)}</span>
                      </div>
                    ))}
                    {filteredRequests.length === 0 && (
                      <div className="px-5 py-8 text-center text-sm text-[#6B7280]">{lang === 'ar' ? 'لا توجد طلبات معلقة' : 'No pending requests'}</div>
                    )}
                  </div>
                </div>

                {/* Recent complaints */}
                <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-[#E2E6EA] flex items-center justify-between">
                    <h3 className="font-bold text-sm text-[#0D1117]">{lang === 'ar' ? 'آخر الشكاوى' : 'Recent Complaints'}</h3>
                    <button onClick={() => setActiveTab('complaints')} className="text-xs text-[#0F5C5C] font-semibold hover:underline flex items-center gap-1">
                      {lang === 'ar' ? 'عرض الكل' : 'View All'} <ChevronRight size={12} />
                    </button>
                  </div>
                  <div className="divide-y divide-[#F0F2F5]">
                    {complaints.slice(0, 5).map(comp => (
                      <div key={comp.id} className="px-5 py-3 flex items-center justify-between hover:bg-[#FAFBFC] transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${comp.priority === 'urgent' || comp.priority === 'high' ? 'bg-red-50' : 'bg-amber-50'}`}>
                            <AlertCircle size={14} className={comp.priority === 'urgent' || comp.priority === 'high' ? 'text-red-600' : 'text-amber-600'} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-[#0D1117] truncate max-w-[180px]">{comp.subject}</p>
                            <p className="text-xs text-[#6B7280]">{comp.category}</p>
                          </div>
                        </div>
                        <span className={`swekt-badge text-xs ${priorityColors[comp.priority] || 'badge-neutral'}`}>{pl(comp.priority)}</span>
                      </div>
                    ))}
                    {complaints.length === 0 && (
                      <div className="px-5 py-8 text-center text-sm text-[#6B7280]">{lang === 'ar' ? 'لا توجد شكاوى' : 'No complaints'}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Companies by subscription status */}
              <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-[#E2E6EA] flex items-center justify-between">
                  <h3 className="font-bold text-sm text-[#0D1117]">{lang === 'ar' ? 'توزيع الشركات حسب حالة الاشتراك' : 'Companies by Subscription Status'}</h3>
                  <button onClick={() => setActiveTab('companies')} className="text-xs text-[#0F5C5C] font-semibold hover:underline flex items-center gap-1">
                    {lang === 'ar' ? 'إدارة الشركات' : 'Manage Companies'} <ChevronRight size={12} />
                  </button>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: lang === 'ar' ? 'نشط' : 'Active', count: companies.filter(c => c.subscription_status === 'active').length, color: 'bg-green-500', textColor: 'text-green-700', bg: 'bg-green-50' },
                      { label: lang === 'ar' ? 'تجريبي' : 'Trial', count: trialCompanies, color: 'bg-blue-500', textColor: 'text-blue-700', bg: 'bg-blue-50' },
                      { label: lang === 'ar' ? 'موقوف' : 'Suspended', count: suspendedCompanies, color: 'bg-red-500', textColor: 'text-red-700', bg: 'bg-red-50' },
                      { label: lang === 'ar' ? 'غير محدد' : 'Unspecified', count: companies.filter(c => !['active', 'trial', 'suspended'].includes(c.subscription_status)).length, color: 'bg-gray-400', textColor: 'text-gray-600', bg: 'bg-gray-50' },
                    ].map(item => (
                      <div key={item.label} className={`${item.bg} rounded-xl p-4 text-center`}>
                        <p className={`text-2xl font-bold ${item.textColor}`}>{item.count}</p>
                        <p className="text-xs text-[#6B7280] mt-1">{item.label}</p>
                        <div className="mt-2 h-1.5 bg-white/60 rounded-full overflow-hidden">
                          <div className={`h-full ${item.color} rounded-full`} style={{ width: companies.length > 0 ? `${(item.count / companies.length) * 100}%` : '0%' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              REVENUE TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'revenue' && (
            <div className="space-y-6">
              <div>
                <h1 className="text-xl font-bold text-[#0D1117]">{lang === 'ar' ? 'إدارة الإيرادات' : 'Revenue Management'}</h1>
                <p className="text-sm text-[#6B7280] mt-0.5">{lang === 'ar' ? 'تتبع الإيرادات والاشتراكات والأداء المالي للمنصة' : 'Track platform revenue, subscriptions, and financial performance'}</p>
              </div>

              {/* Revenue KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-[#0F5C5C] to-[#0A3D3D] rounded-2xl p-5 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <DollarSign size={18} />
                    </div>
                    <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full">MRR</span>
                  </div>
                  <p className="text-3xl font-bold">{estimatedMRR.toLocaleString('en-US')}</p>
                  <p className="text-sm text-white/70 mt-1">{lang === 'ar' ? 'ريال سعودي / شهرياً' : 'SAR / Monthly'}</p>
                  {estimatedMRR === 0 && (
                    <p className="text-xs text-white/50 mt-3">{lang === 'ar' ? 'لا توجد اشتراكات نشطة' : 'No active subscriptions'}</p>
                  )}
                </div>
                <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-5 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <BarChart size={18} />
                    </div>
                    <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full">ARR</span>
                  </div>
                  <p className="text-3xl font-bold">{estimatedARR >= 1000 ? `${(estimatedARR / 1000).toFixed(1)}K` : estimatedARR.toLocaleString('en-US')}</p>
                  <p className="text-sm text-white/70 mt-1">{lang === 'ar' ? 'ريال سعودي / سنوياً' : 'SAR / Annually'}</p>
                  {estimatedARR === 0 && (
                    <p className="text-xs text-white/50 mt-3">{lang === 'ar' ? 'لا توجد اشتراكات نشطة' : 'No active subscriptions'}</p>
                  )}
                </div>
                <div className="bg-gradient-to-br from-amber-500 to-amber-700 rounded-2xl p-5 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <Target size={18} />
                    </div>
                    <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full">ARPU</span>
                  </div>
                  <p className="text-3xl font-bold">{revenueMetrics.arpu.toLocaleString('en-US')}</p>
                  <p className="text-sm text-white/70 mt-1">{lang === 'ar' ? 'متوسط الإيراد لكل شركة' : 'Avg Revenue Per Company'}</p>
                  <div className="flex items-center gap-1 mt-3 text-xs text-white/80">
                    <TrendingUp size={12} />
                    <span>{lang === 'ar' ? 'ريال سعودي / شهر' : 'SAR / month'}</span>
                  </div>
                </div>
              </div>

              {/* Packages revenue breakdown */}
              <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-[#E2E6EA] flex items-center justify-between">
                  <h3 className="font-bold text-sm text-[#0D1117]">{lang === 'ar' ? 'إيرادات الباقات' : 'Package Revenue Breakdown'}</h3>
                  <button onClick={() => setActiveTab('packages')} className="text-xs text-[#0F5C5C] font-semibold hover:underline flex items-center gap-1">
                    {lang === 'ar' ? 'إدارة الباقات' : 'Manage Packages'} <ChevronRight size={12} />
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#F7F8FA] border-b border-[#E2E6EA]">
                        {[lang === 'ar' ? 'الباقة' : 'Package', lang === 'ar' ? 'السعر الشهري' : 'Monthly Price', lang === 'ar' ? 'السعر السنوي' : 'Annual Price', lang === 'ar' ? 'الحد الأقصى للمستخدمين' : 'Max Users', lang === 'ar' ? 'الحالة' : 'Status'].map(h => (
                          <th key={h} className="px-4 py-3 text-start text-xs font-bold text-[#6B7280] uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F0F2F5]">
                      {packages.map(pkg => (
                        <tr key={pkg.id} className="hover:bg-[#FAFBFC] transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {pkg.is_popular && <Star size={12} className="text-amber-500 fill-amber-500" />}
                              <span className="font-semibold text-[#0D1117]">{lang === 'ar' ? pkg.name_ar : pkg.name_en}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-bold text-[#0F5C5C]">{pkg.price_monthly.toLocaleString('en-US')} {lang === 'ar' ? 'ر.س' : 'SAR'}</td>
                          <td className="px-4 py-3 font-bold text-purple-700">{pkg.price_yearly.toLocaleString('en-US')} {lang === 'ar' ? 'ر.س' : 'SAR'}</td>
                          <td className="px-4 py-3 text-[#6B7280]">{pkg.max_users > 0 ? pkg.max_users : '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`swekt-badge text-xs ${pkg.is_active ? 'badge-success' : 'badge-neutral'}`}>
                              {pkg.is_active ? (lang === 'ar' ? 'نشط' : 'Active') : (lang === 'ar' ? 'غير نشط' : 'Inactive')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Revenue metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm p-5">
                  <h3 className="font-bold text-sm text-[#0D1117] mb-4">{lang === 'ar' ? 'مؤشرات الإيرادات' : 'Revenue Indicators'}</h3>
                  <div className="space-y-4">
                    {[
                      { label: lang === 'ar' ? 'معدل قبول الطلبات' : 'Request Approval Rate', value: joinRequests.length > 0 ? Math.round((approvedRequests / joinRequests.length) * 100) : 0, color: 'bg-green-500' },
                      { label: lang === 'ar' ? 'نسبة الشركات النشطة' : 'Active Companies Rate', value: companies.length > 0 ? Math.round((activeCompanies / companies.length) * 100) : 0, color: 'bg-[#0F5C5C]' },
                      { label: lang === 'ar' ? 'نسبة الشركات التجريبية' : 'Trial Companies Rate', value: companies.length > 0 ? Math.round((trialCompanies / companies.length) * 100) : 0, color: 'bg-blue-500' },
                      { label: lang === 'ar' ? 'معدل حل الشكاوى' : 'Complaint Resolution Rate', value: complaints.length > 0 ? Math.round((resolvedComplaints / complaints.length) * 100) : 0, color: 'bg-purple-500' },
                    ].map(m => (
                      <div key={m.label}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-[#6B7280]">{m.label}</span>
                          <span className="text-sm font-bold text-[#0D1117]">{m.value}%</span>
                        </div>
                        <div className="h-2 bg-[#F0F2F5] rounded-full overflow-hidden">
                          <div className={`h-full ${m.color} rounded-full`} style={{ width: `${m.value}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm p-5">
                  <h3 className="font-bold text-sm text-[#0D1117] mb-4">{lang === 'ar' ? 'ملخص مالي' : 'Financial Summary'}</h3>
                  <div className="space-y-3">
                    {[
                      { label: lang === 'ar' ? 'إجمالي الإيرادات الشهرية' : 'Total Monthly Revenue', value: `${estimatedMRR.toLocaleString('en-US')} ${lang === 'ar' ? 'ر.س' : 'SAR'}`, icon: DollarSign, color: 'text-[#0F5C5C]', bg: 'bg-[#EAF4F4]' },
                      { label: lang === 'ar' ? 'إجمالي الإيرادات السنوية' : 'Total Annual Revenue', value: `${estimatedARR.toLocaleString('en-US')} ${lang === 'ar' ? 'ر.س' : 'SAR'}`, icon: TrendingUp, color: 'text-purple-700', bg: 'bg-purple-50' },
                      { label: lang === 'ar' ? 'متوسط الإيراد لكل شركة' : 'Avg Revenue Per Company', value: `${revenueMetrics.arpu.toLocaleString('en-US')} ${lang === 'ar' ? 'ر.س' : 'SAR'}`, icon: Target, color: 'text-amber-700', bg: 'bg-amber-50' },
                      { label: lang === 'ar' ? 'الإيرادات المتوقعة (تجريبي)' : 'Potential Revenue (Trial)', value: `0 ${lang === 'ar' ? 'ر.س' : 'SAR'}`, icon: Zap, color: 'text-blue-700', bg: 'bg-blue-50' },
                    ].map(item => (
                      <div key={item.label} className="flex items-center justify-between py-2 border-b border-[#F0F2F5] last:border-0">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-7 h-7 rounded-lg ${item.bg} flex items-center justify-center`}>
                            <item.icon size={13} className={item.color} />
                          </div>
                          <span className="text-sm text-[#6B7280]">{item.label}</span>
                        </div>
                        <span className="text-sm font-bold text-[#0D1117]">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              ANALYTICS TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div>
                <h1 className="text-xl font-bold text-[#0D1117]">{lang === 'ar' ? 'تحليلات المنصة' : 'Platform Analytics'}</h1>
                <p className="text-sm text-[#6B7280] mt-0.5">{lang === 'ar' ? 'مؤشرات الأداء الرئيسية والإيرادات' : 'Key Performance Indicators & Revenue'}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm p-5">
                  <h3 className="font-bold text-sm text-[#0D1117] mb-4">{lang === 'ar' ? 'توزيع الباقات' : 'Package Distribution'}</h3>
                  <div className="space-y-3">
                    {packages.filter(p => p.is_active).map((pkg, i) => {
                      const count = Math.max(0, activeCompanies - i * Math.floor(activeCompanies / 4));
                      const pct = activeCompanies > 0 ? Math.round((count / activeCompanies) * 100) : 0;
                      const colors = ['bg-[#0F5C5C]', 'bg-purple-600', 'bg-amber-500', 'bg-blue-500'];
                      return (
                        <div key={pkg.id}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <div className={`w-2.5 h-2.5 rounded-full ${colors[i % colors.length]}`} />
                              <span className="text-sm font-semibold text-[#0D1117]">{lang === 'ar' ? pkg.name_ar : pkg.name_en}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-[#6B7280]">{count} {lang === 'ar' ? 'شركة' : 'companies'}</span>
                              <span className="text-xs font-bold text-[#0D1117]">{pct}%</span>
                            </div>
                          </div>
                          <div className="h-2 bg-[#F0F2F5] rounded-full overflow-hidden">
                            <div className={`h-full ${colors[i % colors.length]} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm p-5">
                  <h3 className="font-bold text-sm text-[#0D1117] mb-4">{lang === 'ar' ? 'مؤشرات النمو' : 'Growth Metrics'}</h3>
                  <div className="space-y-4">
                    {[
                      { label: lang === 'ar' ? 'معدل قبول الطلبات' : 'Request Approval Rate', value: joinRequests.length > 0 ? Math.round((approvedRequests / joinRequests.length) * 100) : 0, suffix: '%', color: 'bg-green-500' },
                      { label: lang === 'ar' ? 'معدل حل الشكاوى' : 'Complaint Resolution Rate', value: complaints.length > 0 ? Math.round((resolvedComplaints / complaints.length) * 100) : 0, suffix: '%', color: 'bg-blue-500' },
                      { label: lang === 'ar' ? 'نسبة الشركات النشطة' : 'Active Companies Rate', value: companies.length > 0 ? Math.round((activeCompanies / companies.length) * 100) : 0, suffix: '%', color: 'bg-[#0F5C5C]' },
                      { label: lang === 'ar' ? 'نسبة الباقات النشطة' : 'Active Packages Rate', value: packages.length > 0 ? Math.round((packages.filter(p => p.is_active).length / packages.length) * 100) : 0, suffix: '%', color: 'bg-purple-500' },
                    ].map(m => (
                      <div key={m.label}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-[#6B7280]">{m.label}</span>
                          <span className="text-sm font-bold text-[#0D1117]">{m.value}{m.suffix}</span>
                        </div>
                        <div className="h-2 bg-[#F0F2F5] rounded-full overflow-hidden">
                          <div className={`h-full ${m.color} rounded-full`} style={{ width: `${m.value}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              COMPANIES TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'companies' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="relative max-w-sm flex-1">
                  <Search size={14} className="absolute top-1/2 -translate-y-1/2 end-3 text-[#6B7280]" />
                  <input
                    type="text" value={search} onChange={e => setSearch(e.target.value)}
                    placeholder={lang === 'ar' ? 'بحث بالاسم أو السجل التجاري...' : 'Search by name or CR number...'}
                    className="w-full h-10 bg-white border border-[#E2E6EA] rounded-xl pe-9 ps-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C]"
                  />
                </div>
                <div className="flex items-center gap-2">
                  {(['all', 'active', 'suspended', 'trial'] as const).map(f => (
                    <button
                      key={f}
                      onClick={() => setCompanyFilter(f)}
                      className={`h-9 px-3 rounded-xl text-xs font-bold transition-colors ${
                        companyFilter === f ? 'bg-[#0F5C5C] text-white' : 'bg-white border border-[#E2E6EA] text-[#6B7280] hover:bg-[#F0F2F5]'
                      }`}
                    >
                      {f === 'all' ? (lang === 'ar' ? 'الكل' : 'All') :
                       f === 'active' ? (lang === 'ar' ? 'نشط' : 'Active') :
                       f === 'suspended' ? (lang === 'ar' ? 'موقوف' : 'Suspended') :
                       (lang === 'ar' ? 'تجريبي' : 'Trial')}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#F7F8FA] border-b border-[#E2E6EA]">
                        {[lang === 'ar' ? 'الشركة' : 'Company', lang === 'ar' ? 'السجل التجاري' : 'CR Number', lang === 'ar' ? 'المدينة' : 'City', lang === 'ar' ? 'الاشتراك' : 'Subscription', lang === 'ar' ? 'الحالة' : 'Status', lang === 'ar' ? 'الإجراءات' : 'Actions'].map(h => (
                          <th key={h} className="px-4 py-3 text-start text-xs font-bold text-[#6B7280] uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F0F2F5]">
                      {loading ? (
                        <tr><td colSpan={6} className="text-center py-8"><Loader2 size={18} className="animate-spin mx-auto text-[#0F5C5C]" /></td></tr>
                      ) : filteredCompanies.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-[#6B7280] text-sm">{lang === 'ar' ? 'لا توجد شركات' : 'No companies found'}</td></tr>
                      ) : (
                        filteredCompanies.map(company => (
                          <tr key={company.id} className="hover:bg-[#FAFBFC] transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-[#EAF4F4] flex items-center justify-center text-[#0F5C5C] font-bold text-xs shrink-0">
                                  {company.name_ar?.charAt(0) || '؟'}
                                </div>
                                <div>
                                  <p className="font-semibold text-[#0D1117]">{lang === 'ar' ? company.name_ar : (company.name_en || company.name_ar)}</p>
                                  <p className="text-xs text-[#6B7280]">{company.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-[#6B7280] font-mono text-xs">{company.cr_number || '—'}</td>
                            <td className="px-4 py-3 text-[#6B7280]">{company.city || '—'}</td>
                            <td className="px-4 py-3">
                              <span className={`swekt-badge text-xs ${statusColors[company.subscription_status] || 'badge-neutral'}`}>
                                {sl(company.subscription_status)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`swekt-badge text-xs ${company.is_active ? 'badge-success' : 'badge-danger'}`}>
                                {company.is_active ? (lang === 'ar' ? 'نشط' : 'Active') : (lang === 'ar' ? 'موقوف' : 'Suspended')}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {(company.is_active || company.subscription_status === 'active') ? (
                                /* Approved company: Edit + Password Change + Toggle + Delete */
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => { setEditCompanyForm({ ...company }); setEditCompanyModal(true); }}
                                    className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                                    title={lang === 'ar' ? 'تعديل' : 'Edit'}
                                  >
                                    <Edit2 size={13} />
                                  </button>
                                  <button
                                    onClick={() => { setChangePasswordCompany(company); setNewPassword(''); setConfirmPassword(''); setChangePasswordModal(true); }}
                                    className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition-colors"
                                    title={lang === 'ar' ? 'تغيير كلمة المرور' : 'Change Password'}
                                  >
                                    <Key size={13} />
                                  </button>
                                  <button
                                    onClick={() => handleToggleCompany(company)}
                                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                                    title={lang === 'ar' ? 'تعليق' : 'Suspend'}
                                  >
                                    <PauseCircle size={13} />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirmId(company.id)}
                                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                                    title={lang === 'ar' ? 'حذف' : 'Delete'}
                                  >
                                    <XCircle size={13} />
                                  </button>
                                </div>
                              ) : (
                                /* Non-active company: all action buttons */
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => { setSelectedCompany(company); setCompanyDetailModal(true); }}
                                    className="p-1.5 rounded-lg hover:bg-[#EAF4F4] text-[#0F5C5C] transition-colors"
                                    title={lang === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                                  >
                                    <Eye size={13} />
                                  </button>
                                  <button
                                    onClick={() => { setEditCompanyForm({ ...company }); setEditCompanyModal(true); }}
                                    className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                                    title={lang === 'ar' ? 'تعديل' : 'Edit'}
                                  >
                                    <Edit2 size={13} />
                                  </button>
                                  <button
                                    onClick={() => { setChangePasswordCompany(company); setNewPassword(''); setConfirmPassword(''); setChangePasswordModal(true); }}
                                    className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition-colors"
                                    title={lang === 'ar' ? 'تغيير كلمة المرور' : 'Change Password'}
                                  >
                                    <Key size={13} />
                                  </button>
                                  <button
                                    onClick={() => handleToggleCompany(company)}
                                    className={`p-1.5 rounded-lg transition-colors ${company.is_active ? 'hover:bg-red-50 text-red-600' : 'hover:bg-green-50 text-green-600'}`}
                                    title={company.is_active ? (lang === 'ar' ? 'تعليق' : 'Suspend') : (lang === 'ar' ? 'تفعيل' : 'Activate')}
                                  >
                                    {company.is_active ? <PauseCircle size={13} /> : <PlayCircle size={13} />}
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirmId(company.id)}
                                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                                    title={lang === 'ar' ? 'حذف' : 'Delete'}
                                  >
                                    <XCircle size={13} />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              JOIN REQUESTS TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'join_requests' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-[#0D1117]">{lang === 'ar' ? 'طلبات الانضمام' : 'Join Requests'}</h1>
                <span className="text-sm text-[#6B7280]">{filteredRequests.length} {lang === 'ar' ? 'طلب' : 'requests'}</span>
              </div>
              <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#F7F8FA] border-b border-[#E2E6EA]">
                        {[lang === 'ar' ? 'الشركة' : 'Company', lang === 'ar' ? 'المسؤول' : 'Admin', lang === 'ar' ? 'المدينة' : 'City', lang === 'ar' ? 'تاريخ الطلب' : 'Request Date', lang === 'ar' ? 'الحالة' : 'Status', lang === 'ar' ? 'الإجراءات' : 'Actions'].map(h => (
                          <th key={h} className="px-4 py-3 text-start text-xs font-bold text-[#6B7280] uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F0F2F5]">
                      {loading ? (
                        <tr><td colSpan={6} className="text-center py-8"><Loader2 size={18} className="animate-spin mx-auto text-[#0F5C5C]" /></td></tr>
                      ) : filteredRequests.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-[#6B7280] text-sm">{lang === 'ar' ? 'لا توجد طلبات' : 'No requests found'}</td></tr>
                      ) : (
                        filteredRequests.map(req => (
                          <tr key={req.id} className="hover:bg-[#FAFBFC] transition-colors">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-semibold text-[#0D1117]">{lang === 'ar' ? req.company_name_ar : (req.company_name_en || req.company_name_ar)}</p>
                                <p className="text-xs text-[#6B7280]">{req.cr_number}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-[#0D1117]">{req.admin_name}</p>
                                <p className="text-xs text-[#6B7280]">{req.admin_email}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-[#6B7280]">{req.city || '—'}</td>
                            <td className="px-4 py-3 text-[#6B7280] text-xs">{req.created_at ? new Date(req.created_at).toLocaleDateString('en-US') : '—'}</td>
                            <td className="px-4 py-3">
                              <span className={`swekt-badge text-xs ${statusColors[req.status] || 'badge-neutral'}`}>{sl(req.status)}</span>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => { setSelectedRequest(req); setReviewNotes(req.reviewer_notes || ''); }}
                                className="flex items-center gap-1 h-8 px-3 bg-[#EAF4F4] text-[#0F5C5C] rounded-lg text-xs font-bold hover:bg-[#0F5C5C] hover:text-white transition-colors"
                              >
                                <Eye size={12} />{lang === 'ar' ? 'مراجعة' : 'Review'}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              SUBSCRIPTIONS TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'subscriptions' && (
            <div className="space-y-4">
              <div>
                <h1 className="text-xl font-bold text-[#0D1117]">{lang === 'ar' ? 'إدارة الاشتراكات' : 'Subscription Management'}</h1>
                <p className="text-sm text-[#6B7280] mt-0.5">{lang === 'ar' ? 'متابعة اشتراكات الشركات وحالتها' : 'Monitor company subscriptions and their status'}</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: lang === 'ar' ? 'اشتراكات نشطة' : 'Active Subscriptions', value: companies.filter(c => c.subscription_status === 'active').length, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200', icon: CheckCircle },
                  { label: lang === 'ar' ? 'اشتراكات تجريبية' : 'Trial Subscriptions', value: trialCompanies, color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', icon: Clock },
                  { label: lang === 'ar' ? 'اشتراكات موقوفة' : 'Suspended Subscriptions', value: suspendedCompanies, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: PauseCircle },
                  { label: lang === 'ar' ? 'إجمالي الاشتراكات' : 'Total Subscriptions', value: companies.length, color: 'text-[#0F5C5C]', bg: 'bg-[#EAF4F4]', border: 'border-[#B2D8D8]', icon: CreditCard },
                ].map(s => (
                  <div key={s.label} className={`bg-white border ${s.border} rounded-2xl p-5 shadow-sm`}>
                    <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center mb-3`}>
                      <s.icon size={18} className={s.color} />
                    </div>
                    <p className="text-2xl font-bold text-[#0D1117]">{s.value}</p>
                    <p className="text-sm text-[#6B7280] mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-[#E2E6EA]">
                  <h3 className="font-bold text-sm text-[#0D1117]">{lang === 'ar' ? 'قائمة الاشتراكات' : 'Subscription List'}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#F7F8FA] border-b border-[#E2E6EA]">
                        {[lang === 'ar' ? 'الشركة' : 'Company', lang === 'ar' ? 'حالة الاشتراك' : 'Subscription Status', lang === 'ar' ? 'تاريخ التسجيل' : 'Registration Date', lang === 'ar' ? 'الحالة' : 'Status'].map(h => (
                          <th key={h} className="px-4 py-3 text-start text-xs font-bold text-[#6B7280] uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F0F2F5]">
                      {companies.map(c => (
                        <tr key={c.id} className="hover:bg-[#FAFBFC] transition-colors">
                          <td className="px-4 py-3 font-semibold text-[#0D1117]">{lang === 'ar' ? c.name_ar : (c.name_en || c.name_ar)}</td>
                          <td className="px-4 py-3">
                            <span className={`swekt-badge text-xs ${statusColors[c.subscription_status] || 'badge-neutral'}`}>{sl(c.subscription_status)}</span>
                          </td>
                          <td className="px-4 py-3 text-[#6B7280] text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-US') : '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`swekt-badge text-xs ${c.is_active ? 'badge-success' : 'badge-danger'}`}>
                              {c.is_active ? (lang === 'ar' ? 'نشط' : 'Active') : (lang === 'ar' ? 'موقوف' : 'Suspended')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              COMPLAINTS TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'complaints' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-[#0D1117]">{lang === 'ar' ? 'إدارة الشكاوى' : 'Complaints Management'}</h1>
                <span className="text-sm text-[#6B7280]">{filteredComplaints.length} {lang === 'ar' ? 'شكوى' : 'complaints'}</span>
              </div>
              <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#F7F8FA] border-b border-[#E2E6EA]">
                        {[lang === 'ar' ? 'الموضوع' : 'Subject', lang === 'ar' ? 'الفئة' : 'Category', lang === 'ar' ? 'الأولوية' : 'Priority', lang === 'ar' ? 'الحالة' : 'Status', lang === 'ar' ? 'التاريخ' : 'Date', lang === 'ar' ? 'الإجراءات' : 'Actions'].map(h => (
                          <th key={h} className="px-4 py-3 text-start text-xs font-bold text-[#6B7280] uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F0F2F5]">
                      {loading ? (
                        <tr><td colSpan={6} className="text-center py-8"><Loader2 size={18} className="animate-spin mx-auto text-[#0F5C5C]" /></td></tr>
                      ) : filteredComplaints.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-[#6B7280] text-sm">{lang === 'ar' ? 'لا توجد شكاوى' : 'No complaints found'}</td></tr>
                      ) : (
                        filteredComplaints.map(comp => (
                          <tr key={comp.id} className="hover:bg-[#FAFBFC] transition-colors">
                            <td className="px-4 py-3 font-semibold text-[#0D1117] max-w-[200px] truncate">{comp.subject}</td>
                            <td className="px-4 py-3 text-[#6B7280]">{comp.category}</td>
                            <td className="px-4 py-3">
                              <span className={`swekt-badge text-xs ${priorityColors[comp.priority] || 'badge-neutral'}`}>{pl(comp.priority)}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`swekt-badge text-xs ${statusColors[comp.status] || 'badge-neutral'}`}>{sl(comp.status)}</span>
                            </td>
                            <td className="px-4 py-3 text-[#6B7280] text-xs">{comp.created_at ? new Date(comp.created_at).toLocaleDateString('en-US') : '—'}</td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => setSelectedComplaint(comp)}
                                className="flex items-center gap-1 h-8 px-3 bg-[#EAF4F4] text-[#0F5C5C] rounded-lg text-xs font-bold hover:bg-[#0F5C5C] hover:text-white transition-colors"
                              >
                                <Eye size={12} />{lang === 'ar' ? 'معالجة' : 'Handle'}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              CONTACT MESSAGES TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'contact_messages' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h1 className="text-xl font-bold text-[#0D1117]">{lang === 'ar' ? 'رسائل التواصل' : 'Contact Messages'}</h1>
                  <p className="text-sm text-[#6B7280] mt-0.5">{lang === 'ar' ? 'الرسائل الواردة من زوار المنصة' : 'Messages received from platform visitors'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {newContactMessages > 0 && (
                    <span className="flex items-center gap-1.5 h-8 px-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl text-xs font-bold">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                      {newContactMessages} {lang === 'ar' ? 'جديدة' : 'new'}
                    </span>
                  )}
                  <span className="text-sm text-[#6B7280]">{filteredContactMessages.length} {lang === 'ar' ? 'رسالة' : 'messages'}</span>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: lang === 'ar' ? 'جديدة' : 'New', count: contactMessages.filter(m => m.status === 'new').length, color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
                  { label: lang === 'ar' ? 'قيد المعالجة' : 'In Progress', count: contactMessages.filter(m => m.status === 'in_progress').length, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
                  { label: lang === 'ar' ? 'تمت المعالجة' : 'Resolved', count: contactMessages.filter(m => m.status === 'resolved').length, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
                ].map(s => (
                  <div key={s.label} className={`${s.bg} border ${s.border} rounded-xl p-4 text-center`}>
                    <p className={`text-2xl font-black ${s.color}`}>{s.count}</p>
                    <p className={`text-xs font-semibold mt-0.5 ${s.color}`}>{s.label}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#F7F8FA] border-b border-[#E2E6EA]">
                        {[
                          lang === 'ar' ? 'المرسل' : 'Sender',
                          lang === 'ar' ? 'الهاتف' : 'Phone',
                          lang === 'ar' ? 'عنوان التواصل' : 'Subject',
                          lang === 'ar' ? 'الحالة' : 'Status',
                          lang === 'ar' ? 'التاريخ' : 'Date',
                          lang === 'ar' ? 'الإجراءات' : 'Actions',
                        ].map(h => (
                          <th key={h} className="px-4 py-3 text-start text-xs font-bold text-[#6B7280] uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F0F2F5]">
                      {loading ? (
                        <tr><td colSpan={6} className="text-center py-8"><Loader2 size={18} className="animate-spin mx-auto text-[#0F5C5C]" /></td></tr>
                      ) : filteredContactMessages.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-[#6B7280] text-sm">{lang === 'ar' ? 'لا توجد رسائل' : 'No messages found'}</td></tr>
                      ) : (
                        filteredContactMessages.map(msg => (
                          <tr key={msg.id} className={`hover:bg-[#FAFBFC] transition-colors ${msg.status === 'new' ? 'bg-blue-50/30' : ''}`}>
                            <td className="px-4 py-3">
                              <p className="font-semibold text-[#0D1117]">{msg.name}</p>
                              <p className="text-xs text-[#6B7280]">{msg.email}</p>
                            </td>
                            <td className="px-4 py-3 text-[#6B7280] text-xs font-mono">{msg.phone}</td>
                            <td className="px-4 py-3 font-medium text-[#0D1117] max-w-[200px] truncate">{msg.subject}</td>
                            <td className="px-4 py-3">
                              <span className={`swekt-badge text-xs ${
                                msg.status === 'new' ? 'badge-info' :
                                msg.status === 'in_progress' ? 'badge-warning' :
                                msg.status === 'resolved' ? 'badge-success' : 'badge-neutral'
                              }`}>
                                {msg.status === 'new' ? (lang === 'ar' ? 'جديدة' : 'New') :
                                 msg.status === 'in_progress' ? (lang === 'ar' ? 'قيد المعالجة' : 'In Progress') :
                                 msg.status === 'resolved' ? (lang === 'ar' ? 'تمت المعالجة' : 'Resolved') :
                                 msg.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-[#6B7280] text-xs">{msg.created_at ? new Date(msg.created_at).toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US') : '—'}</td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => { setSelectedContactMessage(msg); setContactMessageNotes(msg.admin_notes || ''); }}
                                className="flex items-center gap-1 h-8 px-3 bg-[#EAF4F4] text-[#0F5C5C] rounded-lg text-xs font-bold hover:bg-[#0F5C5C] hover:text-white transition-colors"
                              >
                                <Eye size={12} />{lang === 'ar' ? 'عرض' : 'View'}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              PACKAGES TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'packages' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold text-[#0D1117]">{lang === 'ar' ? 'إدارة الباقات' : 'Package Management'}</h1>
                  <p className="text-sm text-[#6B7280] mt-0.5">{lang === 'ar' ? 'إدارة باقات الاشتراك والأسعار' : 'Manage subscription packages and pricing'}</p>
                </div>
                <button
                  onClick={() => { setEditPackage(null); setPkgForm({ is_active: true, features: [], max_users: 5, max_branches: 1, sort_order: packages.length + 1 }); setPackageModal(true); }}
                  className="flex items-center gap-1.5 h-9 px-4 bg-[#0F5C5C] text-white rounded-xl text-sm font-bold hover:bg-[#0A3D3D] transition-colors"
                >
                  <Plus size={14} />{lang === 'ar' ? 'باقة جديدة' : 'New Package'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {packages.map(pkg => (
                  <div key={pkg.id} className={`bg-white border rounded-2xl shadow-sm overflow-hidden ${pkg.is_popular ? 'border-[#0F5C5C] ring-1 ring-[#0F5C5C]/20' : 'border-[#E2E6EA]'}`}>
                    {pkg.is_popular && (
                      <div className="bg-[#0F5C5C] text-white text-center text-xs font-bold py-1.5 flex items-center justify-center gap-1">
                        <Star size={11} className="fill-white" />
                        {lang === 'ar' ? 'الأكثر طلباً' : 'Most Popular'}
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-[#0D1117]">{lang === 'ar' ? pkg.name_ar : pkg.name_en}</h3>
                          <p className="text-xs text-[#6B7280] mt-0.5">{pkg.max_users > 0 ? `${pkg.max_users} ${lang === 'ar' ? 'مستخدم' : 'users'}` : lang === 'ar' ? 'إضافة' : 'Add-on'}</p>
                        </div>
                        <span className={`swekt-badge text-xs ${pkg.is_active ? 'badge-success' : 'badge-neutral'}`}>
                          {pkg.is_active ? (lang === 'ar' ? 'نشط' : 'Active') : (lang === 'ar' ? 'غير نشط' : 'Inactive')}
                        </span>
                      </div>
                      <div className="mb-4">
                        <p className="text-2xl font-black text-[#0F5C5C]">{pkg.price_yearly.toLocaleString('en-US')} <span className="text-sm font-semibold text-[#6B7280]">{lang === 'ar' ? 'ر.س/سنة' : 'SAR/yr'}</span></p>
                        <p className="text-xs text-[#9CA3AF]">{pkg.price_monthly.toLocaleString('en-US')} {lang === 'ar' ? 'ر.س/شهر' : 'SAR/mo'}</p>
                      </div>
                      <div className="space-y-1 mb-4 max-h-32 overflow-y-auto">
                        {(pkg.features || []).slice(0, 5).map((f, i) => (
                          <div key={i} className="flex items-center gap-1.5 text-xs text-[#6B7280]">
                            <Check size={10} className="text-green-600 shrink-0" />
                            <span>{f}</span>
                          </div>
                        ))}
                        {(pkg.features || []).length > 5 && (
                          <p className="text-xs text-[#9CA3AF] ps-4">+{(pkg.features || []).length - 5} {lang === 'ar' ? 'ميزة أخرى' : 'more features'}</p>
                        )}
                      </div>
                      <button
                        onClick={() => { setEditPackage(pkg); setPkgForm({ ...pkg }); setPackageModal(true); }}
                        className="w-full h-9 border border-[#E2E6EA] rounded-xl text-sm font-bold text-[#0F5C5C] hover:bg-[#EAF4F4] transition-colors flex items-center justify-center gap-1.5"
                      >
                        <Edit2 size={13} />{lang === 'ar' ? 'تعديل الباقة' : 'Edit Package'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              AUTH ADMIN TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'auth_admin' && (
            <AuthAdminDashboard lang={lang as 'ar' | 'en'} />
          )}

          {/* ══════════════════════════════════════════════════════════════════
              AUTH ALERTS TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'auth_alerts' && (
            <AuthAlertCenter lang={lang as 'ar' | 'en'} />
          )}

          {/* ══════════════════════════════════════════════════════════════════
              ADMIN ACTION LOGS TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'admin_logs' && (
            <AdminActionLogsPanel lang={lang as 'ar' | 'en'} />
          )}

          {/* ══════════════════════════════════════════════════════════════════
              PLATFORM USERS TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'platform_users' && (
            <PlatformUsersPanel lang={lang as 'ar' | 'en'} />
          )}

          {/* ══════════════════════════════════════════════════════════════════
              SYSTEM HEALTH TAB
          ══════════════════════════════════════════════════════════════════ */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <div>
                <h1 className="text-xl font-bold text-[#0D1117]">{lang === 'ar' ? 'صحة النظام' : 'System Health'}</h1>
                <p className="text-sm text-[#6B7280] mt-0.5">{lang === 'ar' ? 'مراقبة أداء وصحة منصة نيكسورا' : 'Monitor Nexora platform performance and health'}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm p-5">
                  <h3 className="font-bold text-sm text-[#0D1117] mb-4 flex items-center gap-2">
                    <Server size={15} className="text-[#0F5C5C]" />
                    {lang === 'ar' ? 'حالة الخدمات' : 'Service Status'}
                  </h3>
                  <HealthItem label={lang === 'ar' ? 'قاعدة البيانات' : 'Database'} status="ok" value="Supabase PostgreSQL — 99.9% uptime" icon={Database} />
                  <HealthItem label={lang === 'ar' ? 'المصادقة' : 'Authentication'} status="ok" value="Supabase Auth — Active" icon={Lock} />
                  <HealthItem label={lang === 'ar' ? 'التخزين' : 'Storage'} status="ok" value="Supabase Storage — Active" icon={HardDrive} />
                  <HealthItem label={lang === 'ar' ? 'الوقت الفعلي' : 'Realtime'} status="ok" value="WebSocket — Connected" icon={Wifi} />
                  <HealthItem label={lang === 'ar' ? 'الواجهة الأمامية' : 'Frontend'} status="ok" value="Next.js 15 — Running" icon={Globe} />
                  <HealthItem label={lang === 'ar' ? 'الأداء' : 'Performance'} status="ok" value="Response time < 200ms" icon={Zap} />
                </div>
                <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm p-5">
                  <h3 className="font-bold text-sm text-[#0D1117] mb-4 flex items-center gap-2">
                    <Activity size={15} className="text-purple-600" />
                    {lang === 'ar' ? 'إحصائيات النظام' : 'System Statistics'}
                  </h3>
                  <div className="space-y-3">
                    {[
                      { label: lang === 'ar' ? 'إجمالي الشركات المسجلة' : 'Total Registered Companies', value: companies.length, icon: Building2, color: 'text-[#0F5C5C]', bg: 'bg-[#EAF4F4]' },
                      { label: lang === 'ar' ? 'إجمالي طلبات الانضمام' : 'Total Join Requests', value: joinRequests.length, icon: FileText, color: 'text-blue-700', bg: 'bg-blue-50' },
                      { label: lang === 'ar' ? 'إجمالي الشكاوى' : 'Total Complaints', value: complaints.length, icon: MessageSquare, color: 'text-amber-700', bg: 'bg-amber-50' },
                      { label: lang === 'ar' ? 'الباقات المتاحة' : 'Available Packages', value: packages.length, icon: Package, color: 'text-purple-700', bg: 'bg-purple-50' },
                      { label: lang === 'ar' ? 'الشركات النشطة' : 'Active Companies', value: activeCompanies, icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-50' },
                    ].map(item => (
                      <div key={item.label} className="flex items-center justify-between py-2 border-b border-[#F0F2F5] last:border-0">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-7 h-7 rounded-lg ${item.bg} flex items-center justify-center`}>
                            <item.icon size={13} className={item.color} />
                          </div>
                          <span className="text-sm text-[#6B7280]">{item.label}</span>
                        </div>
                        <span className="text-sm font-bold text-[#0D1117]">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ── Review Request Modal ─────────────────────────────────────────────── */}
      <Modal isOpen={!!selectedRequest} onClose={() => { setSelectedRequest(null); setReviewNotes(''); }} title={lang === 'ar' ? 'مراجعة طلب الانضمام' : 'Review Join Request'} size="md">
        {selectedRequest && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 bg-[#F7F8FA] rounded-xl p-4">
              {[
                { label: lang === 'ar' ? 'اسم الشركة' : 'Company Name', value: lang === 'ar' ? selectedRequest.company_name_ar : (selectedRequest.company_name_en || selectedRequest.company_name_ar) },
                { label: lang === 'ar' ? 'السجل التجاري' : 'CR Number', value: selectedRequest.cr_number },
                { label: lang === 'ar' ? 'الرقم الضريبي' : 'VAT Number', value: selectedRequest.vat_number },
                { label: lang === 'ar' ? 'المدينة' : 'City', value: selectedRequest.city },
                { label: lang === 'ar' ? 'المسؤول' : 'Admin', value: selectedRequest.admin_name },
                { label: lang === 'ar' ? 'البريد الإلكتروني' : 'Email', value: selectedRequest.admin_email },
              ].map(f => (
                <div key={f.label}>
                  <p className="text-xs text-[#6B7280] mb-0.5">{f.label}</p>
                  <p className="text-sm font-semibold text-[#0D1117]">{f.value || '—'}</p>
                </div>
              ))}
            </div>

            {/* Selected Package */}
            {(selectedRequest.requested_package_name || selectedRequest.requested_package_id) && (
              <div className="flex items-center gap-3 bg-[#EAF4F4] border border-[#B2D8D8] rounded-xl p-3">
                <div className="w-8 h-8 rounded-lg bg-[#0F5C5C] flex items-center justify-center shrink-0">
                  <Package size={14} className="text-white" />
                </div>
                <div>
                  <p className="text-xs text-[#6B7280]">{lang === 'ar' ? 'الباقة المختارة' : 'Selected Package'}</p>
                  <p className="text-sm font-bold text-[#0F5C5C]">{selectedRequest.requested_package_name || selectedRequest.requested_package_id}</p>
                </div>
              </div>
            )}

            {/* Document completeness warning banner */}
            {(() => {
              const docs = [
                selectedRequest.vat_document_url,
                selectedRequest.cr_document_url,
                selectedRequest.national_address_document_url,
                selectedRequest.id_document_url,
              ];
              const missing = docs.filter(d => !d).length;
              if (missing === 0) return null;
              const isBlocked = missing > 2;
              return (
                <div className={`flex items-start gap-2.5 ${isBlocked ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} border rounded-xl px-3 py-2.5`}>
                  <AlertTriangle size={15} className={`${isBlocked ? 'text-red-600' : 'text-amber-600'} shrink-0 mt-0.5`} />
                  <div>
                    <p className={`text-sm font-bold ${isBlocked ? 'text-red-700' : 'text-amber-700'}`}>
                      {lang === 'ar'
                        ? `${isBlocked ? 'تحذير' : 'تنبيه'}: ${missing} مستند${missing > 1 ? 'ات' : ''} مفقود${missing > 1 ? 'ة' : ''}`
                        : `${isBlocked ? 'Warning' : 'Notice'}: ${missing} missing document${missing > 1 ? 's' : ''}`}
                    </p>
                    <p className={`text-xs ${isBlocked ? 'text-red-600' : 'text-amber-600'} mt-0.5`}>
                      {isBlocked
                        ? (lang === 'ar' ? 'لا يمكن الموافقة — يجب رفع 3 مستندات على الأقل.' : 'Cannot approve — at least 3 documents must be uploaded.')
                        : (lang === 'ar' ? 'يمكن الموافقة مع نقص مستند أو مستندين — سيُطلب منهم إرسالها لاحقاً.' : 'Approval allowed with up to 2 missing documents — they will be asked to submit them later.')}
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* ── Mandatory Documents Section ── */}
            <div className="rounded-xl border border-[#E2E6EA] overflow-hidden">
              <div className="px-4 py-2.5 bg-[#F7F8FA] border-b border-[#E2E6EA] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText size={13} className="text-[#0F5C5C]" />
                  <span className="text-xs font-bold text-[#0D1117]">{lang === 'ar' ? 'المستندات الرسمية المطلوبة' : 'Required Official Documents'}</span>
                </div>
                {/* Document completeness badge */}
                {(() => {
                  const docs = [
                    selectedRequest.vat_document_url,
                    selectedRequest.cr_document_url,
                    selectedRequest.national_address_document_url,
                    selectedRequest.id_document_url,
                  ];
                  const uploaded = docs.filter(Boolean).length;
                  return (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      uploaded === 4 ? 'bg-green-100 text-green-700' :
                      uploaded > 0 ? 'bg-amber-100 text-amber-700': 'bg-red-100 text-red-700'
                    }`}>
                      {uploaded}/4 {lang === 'ar' ? 'مرفوع' : 'uploaded'}
                    </span>
                  );
                })()}
              </div>
              <div className="p-3 space-y-2">
                {[
                  {
                    url: selectedRequest.vat_document_url,
                    label: lang === 'ar' ? 'شهادة التسجيل الضريبي' : 'VAT Certificate',
                    icon: FileText,
                    color: 'text-purple-600',
                    bg: 'bg-purple-50',
                  },
                  {
                    url: selectedRequest.cr_document_url,
                    label: lang === 'ar' ? 'وثيقة السجل التجاري' : 'CR Certificate',
                    icon: FileText,
                    color: 'text-blue-600',
                    bg: 'bg-blue-50',
                  },
                  {
                    url: selectedRequest.national_address_document_url,
                    label: lang === 'ar' ? 'وثيقة العنوان الوطني' : 'National Address Document',
                    icon: MapPin,
                    color: 'text-teal-600',
                    bg: 'bg-teal-50',
                  },
                  {
                    url: selectedRequest.id_document_url,
                    label: lang === 'ar' ? 'الهوية الوطنية / الإقامة' : 'National ID / Iqama',
                    icon: Users,
                    color: 'text-orange-600',
                    bg: 'bg-orange-50',
                  },
                ].map(doc => (
                  <div key={doc.label} className={`flex items-center justify-between py-2 px-3 rounded-lg border transition-colors ${
                    doc.url ? 'border-green-200 bg-green-50/40' : 'border-red-200 bg-red-50/30'
                  }`}>
                    <div className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-lg ${doc.bg} flex items-center justify-center shrink-0`}>
                        <doc.icon size={13} className={doc.color} />
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-[#0D1117]">{doc.label}</span>
                        {!doc.url && (
                          <p className="text-[10px] text-red-600 font-medium">{lang === 'ar' ? 'لم يُرفع' : 'Not uploaded'}</p>
                        )}
                      </div>
                    </div>
                    {doc.url ? (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 h-7 px-3 bg-[#EAF4F4] text-[#0F5C5C] rounded-lg text-xs font-bold hover:bg-[#0F5C5C] hover:text-white transition-colors"
                      >
                        <Eye size={11} />{lang === 'ar' ? 'عرض' : 'View'}
                      </a>
                    ) : (
                      <span className="flex items-center gap-1 h-7 px-2 bg-red-100 text-red-600 rounded-lg text-[10px] font-bold">
                        <AlertTriangle size={10} />{lang === 'ar' ? 'مفقود' : 'Missing'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">
                {lang === 'ar' ? 'ملاحظات المراجع' : 'Reviewer Notes'}
                {' '}
                <span className="text-xs text-[#6B7280] font-normal">
                  ({lang === 'ar' ? 'ستُرسل للمالك عند طلب معلومات إضافية' : 'sent to owner when requesting info'})
                </span>
              </label>
              <textarea
                value={reviewNotes}
                onChange={e => setReviewNotes(e.target.value)}
                rows={3}
                className="w-full bg-[#F0F2F5] border border-[#E2E6EA] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C]"
                placeholder={lang === 'ar' ? 'أضف ملاحظاتك هنا... (ستُرسل للمالك عبر البريد الإلكتروني)' : 'Add your notes here... (will be sent to owner via email)'}
              />
            </div>
            <div className="flex items-center gap-2 justify-end flex-wrap">
              <button onClick={() => { setSelectedRequest(null); setReviewNotes(''); }} className="h-9 px-4 bg-[#EEF0F3] text-[#6B7280] rounded-lg text-sm font-semibold">{lang === 'ar' ? 'إغلاق' : 'Close'}</button>
              <button onClick={() => handleReviewRequest(selectedRequest.id, 'info_required')} disabled={reviewLoading} className="flex items-center gap-1 h-9 px-4 bg-blue-100 text-blue-700 rounded-lg text-sm font-bold hover:bg-blue-200 transition-colors">
                <Info size={13} />{lang === 'ar' ? 'طلب معلومات (إيميل)' : 'Request Info (Email)'}
              </button>
              <button onClick={() => handleReviewRequest(selectedRequest.id, 'rejected')} disabled={reviewLoading} className="flex items-center gap-1 h-9 px-4 bg-red-100 text-red-700 rounded-lg text-sm font-bold hover:bg-red-200 transition-colors">
                <XCircle size={13} />{lang === 'ar' ? 'رفض' : 'Reject'}
              </button>
              <button
                onClick={() => {
                  const docs = [
                    selectedRequest.vat_document_url,
                    selectedRequest.cr_document_url,
                    selectedRequest.national_address_document_url,
                    selectedRequest.id_document_url,
                  ];
                  const missing = docs.filter(d => !d).length;
                  if (missing > 2) {
                    toast.error(lang === 'ar' ? 'لا يمكن الموافقة — يجب رفع 3 مستندات على الأقل' : 'Cannot approve — at least 3 documents must be uploaded');
                    return;
                  }
                  if (missing > 0) {
                    toast.warning(lang === 'ar' ? `تنبيه: ${missing} مستند${missing > 1 ? 'ات' : ''} مفقود${missing > 1 ? 'ة' : ''} — سيُطلب من الشركة إرسالها لاحقاً` : `Notice: ${missing} missing document${missing > 1 ? 's' : ''} — company will be asked to submit later`);
                  }
                  handleReviewRequest(selectedRequest.id, 'approved');
                }}
                disabled={reviewLoading}
                className={`flex items-center gap-1 h-9 px-4 rounded-lg text-sm font-bold transition-colors ${
                  (() => {
                    const docs = [selectedRequest.vat_document_url, selectedRequest.cr_document_url, selectedRequest.national_address_document_url, selectedRequest.id_document_url];
                    const missing = docs.filter(d => !d).length;
                    return missing <= 2 ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed';
                  })()
                }`}
                title={
                  (() => {
                    const docs = [selectedRequest.vat_document_url, selectedRequest.cr_document_url, selectedRequest.national_address_document_url, selectedRequest.id_document_url];
                    const missing = docs.filter(d => !d).length;
                    return missing > 2 ? (lang === 'ar' ? 'يجب رفع 3 مستندات على الأقل قبل الموافقة' : 'At least 3 documents must be uploaded before approving') : undefined;
                  })()
                }
              >
                {reviewLoading ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                {lang === 'ar' ? 'قبول' : 'Approve'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Complaint Modal ──────────────────────────────────────────────────── */}
      <Modal isOpen={!!selectedComplaint} onClose={() => setSelectedComplaint(null)} title={lang === 'ar' ? 'معالجة الشكوى' : 'Handle Complaint'} size="md">
        {selectedComplaint && (
          <div className="space-y-4">
            <div className="bg-[#F7F8FA] rounded-xl p-4">
              <h3 className="font-bold text-[#0D1117] mb-2">{selectedComplaint.subject}</h3>
              <p className="text-sm text-[#6B7280]">{selectedComplaint.description}</p>
              <div className="flex items-center gap-2 mt-3">
                <span className={`swekt-badge text-xs ${priorityColors[selectedComplaint.priority] || 'badge-neutral'}`}>{pl(selectedComplaint.priority)}</span>
                <span className={`swekt-badge text-xs ${statusColors[selectedComplaint.status] || 'badge-neutral'}`}>{sl(selectedComplaint.status)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => setSelectedComplaint(null)} className="h-9 px-4 bg-[#EEF0F3] text-[#6B7280] rounded-lg text-sm font-semibold">{lang === 'ar' ? 'إغلاق' : 'Close'}</button>
              <button onClick={() => handleResolveComplaint(selectedComplaint.id, 'in_progress')} className="h-9 px-4 bg-blue-100 text-blue-700 rounded-lg text-sm font-bold hover:bg-blue-200 transition-colors">{lang === 'ar' ? 'قيد المعالجة' : 'In Progress'}</button>
              <button onClick={() => handleResolveComplaint(selectedComplaint.id, 'resolved')} className="h-9 px-4 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 transition-colors">{lang === 'ar' ? 'تم الحل' : 'Resolved'}</button>
              <button onClick={() => handleResolveComplaint(selectedComplaint.id, 'closed')} className="h-9 px-4 bg-[#EEF0F3] text-[#6B7280] rounded-lg text-sm font-bold hover:bg-gray-200 transition-colors">{lang === 'ar' ? 'إغلاق' : 'Close'}</button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Company Detail Modal ─────────────────────────────────────────────── */}
      <Modal isOpen={companyDetailModal} onClose={() => { setCompanyDetailModal(false); setSelectedCompany(null); }} title={lang === 'ar' ? 'تفاصيل الشركة' : 'Company Details'} size="md">
        {selectedCompany && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 bg-[#F7F8FA] rounded-xl p-4">
              {[
                { label: lang === 'ar' ? 'الاسم (عربي)' : 'Name (Arabic)', value: selectedCompany.name_ar },
                { label: lang === 'ar' ? 'الاسم (إنجليزي)' : 'Name (English)', value: selectedCompany.name_en },
                { label: lang === 'ar' ? 'السجل التجاري' : 'CR Number', value: selectedCompany.cr_number },
                { label: lang === 'ar' ? 'الرقم الضريبي' : 'VAT Number', value: selectedCompany.vat_number },
                { label: lang === 'ar' ? 'المدينة' : 'City', value: selectedCompany.city },
                { label: lang === 'ar' ? 'الهاتف' : 'Phone', value: selectedCompany.phone },
                { label: lang === 'ar' ? 'البريد الإلكتروني' : 'Email', value: selectedCompany.email },
                { label: lang === 'ar' ? 'حالة الاشتراك' : 'Subscription', value: sl(selectedCompany.subscription_status) },
              ].map(f => (
                <div key={f.label}>
                  <p className="text-xs text-[#6B7280] mb-0.5">{f.label}</p>
                  <p className="text-sm font-semibold text-[#0D1117]">{f.value || '—'}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 justify-end flex-wrap">
              <button onClick={() => setDeleteConfirmId(selectedCompany.id)} className="h-9 px-4 bg-red-100 text-red-700 rounded-lg text-sm font-bold hover:bg-red-200 transition-colors flex items-center gap-1.5">
                <XCircle size={13} />{lang === 'ar' ? 'حذف' : 'Delete'}
              </button>
              <button
                onClick={() => handleResetCompanyAdminPassword(selectedCompany)}
                disabled={resetCompanyAdminLoading === selectedCompany.id}
                className="h-9 px-4 bg-amber-100 text-amber-700 rounded-lg text-sm font-bold hover:bg-amber-200 transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                {resetCompanyAdminLoading === selectedCompany.id ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                {lang === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
              </button>
              <button onClick={() => handleToggleCompany(selectedCompany)} className={`h-9 px-4 rounded-lg text-sm font-bold flex items-center gap-1.5 transition-colors ${selectedCompany.is_active ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                {selectedCompany.is_active ? <PauseCircle size={13} /> : <PlayCircle size={13} />}
                {selectedCompany.is_active ? (lang === 'ar' ? 'تعليق' : 'Suspend') : (lang === 'ar' ? 'تفعيل' : 'Activate')}
              </button>
              {/* Reactivation button for read-only/suspended companies */}
              {(selectedCompany.is_read_only || selectedCompany.subscription_status === 'suspended') && (
                <button
                  onClick={() => handleReactivateCompany(selectedCompany)}
                  className="h-9 px-4 bg-purple-600 text-white rounded-lg text-sm font-bold flex items-center gap-1.5 hover:bg-purple-700 transition-colors"
                  title={lang === 'ar' ? 'إعادة تفعيل الشركة (مدير المنصة فقط)' : 'Reactivate Company (Platform Owner Only)'}
                >
                  <Crown size={13} />
                  {lang === 'ar' ? 'إعادة التفعيل' : 'Reactivate'}
                </button>
              )}
              <button onClick={() => { setEditCompanyForm({ ...selectedCompany }); setCompanyDetailModal(false); setEditCompanyModal(true); }} className="h-9 px-4 bg-blue-600 text-white rounded-lg text-sm font-bold flex items-center gap-1.5 hover:bg-blue-700 transition-colors">
                <Edit2 size={13} />{lang === 'ar' ? 'تعديل' : 'Edit'}
              </button>
              <button onClick={() => { setCompanyDetailModal(false); setSelectedCompany(null); }} className="h-9 px-4 bg-[#EEF0F3] text-[#6B7280] rounded-lg text-sm font-semibold">{lang === 'ar' ? 'إغلاق' : 'Close'}</button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Contact Message Detail Modal ─────────────────────────────────────── */}
      <Modal isOpen={!!selectedContactMessage} onClose={() => { setSelectedContactMessage(null); setContactMessageNotes(''); }} title={lang === 'ar' ? 'تفاصيل رسالة التواصل' : 'Contact Message Details'} size="md">
        {selectedContactMessage && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: lang === 'ar' ? 'الاسم' : 'Name', value: selectedContactMessage.name },
                { label: lang === 'ar' ? 'الهاتف' : 'Phone', value: selectedContactMessage.phone },
                { label: lang === 'ar' ? 'البريد الإلكتروني' : 'Email', value: selectedContactMessage.email },
                { label: lang === 'ar' ? 'التاريخ' : 'Date', value: selectedContactMessage.created_at ? new Date(selectedContactMessage.created_at).toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US') : '—' },
              ].map(f => (
                <div key={f.label} className="bg-[#F7F8FA] rounded-xl p-3">
                  <p className="text-xs text-[#6B7280] mb-0.5">{f.label}</p>
                  <p className="text-sm font-semibold text-[#0D1117]">{f.value || '—'}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#F7F8FA] rounded-xl p-3">
              <p className="text-xs text-[#6B7280] mb-0.5">{lang === 'ar' ? 'عنوان التواصل' : 'Subject'}</p>
              <p className="text-sm font-semibold text-[#0D1117]">{selectedContactMessage.subject}</p>
            </div>
            <div className="bg-[#F7F8FA] rounded-xl p-3">
              <p className="text-xs text-[#6B7280] mb-1">{lang === 'ar' ? 'الشرح والتفاصيل' : 'Description'}</p>
              <p className="text-sm text-[#0D1117] leading-relaxed whitespace-pre-wrap">{selectedContactMessage.description}</p>
            </div>
            {selectedContactMessage.attachment_url && (
              <div className="bg-[#F7F8FA] rounded-xl p-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B7280] mb-0.5">{lang === 'ar' ? 'المستند المرفق' : 'Attached Document'}</p>
                  <p className="text-sm font-semibold text-[#0D1117]">{selectedContactMessage.attachment_name || (lang === 'ar' ? 'ملف مرفق' : 'Attached file')}</p>
                </div>
                <a href={selectedContactMessage.attachment_url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 h-8 px-3 bg-[#0F5C5C] text-white rounded-lg text-xs font-bold hover:bg-[#0A3D3D] transition-colors">
                  <Eye size={12} />{lang === 'ar' ? 'عرض' : 'View'}
                </a>
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">{lang === 'ar' ? 'ملاحظات المدير' : 'Admin Notes'}</label>
              <textarea
                value={contactMessageNotes}
                onChange={e => setContactMessageNotes(e.target.value)}
                rows={3}
                placeholder={lang === 'ar' ? 'أضف ملاحظاتك هنا...' : 'Add your notes here...'}
                className="w-full bg-[#F0F2F5] border border-[#E2E6EA] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C]"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => handleUpdateContactMessage(selectedContactMessage.id, 'in_progress')} disabled={contactMessageUpdating}
                className="flex items-center gap-1.5 h-9 px-4 bg-amber-100 text-amber-700 rounded-lg text-sm font-bold hover:bg-amber-200 disabled:opacity-50 transition-colors">
                {lang === 'ar' ? 'قيد المعالجة' : 'In Progress'}
              </button>
              <button onClick={() => handleUpdateContactMessage(selectedContactMessage.id, 'resolved')} disabled={contactMessageUpdating}
                className="flex items-center gap-1.5 h-9 px-4 bg-green-100 text-green-700 rounded-lg text-sm font-bold hover:bg-green-200 disabled:opacity-50 transition-colors">
                {contactMessageUpdating ? <Loader2 size={13} className="animate-spin" /> : null}
                {lang === 'ar' ? 'تمت المعالجة' : 'Mark Resolved'}
              </button>
              <button onClick={() => { setSelectedContactMessage(null); setContactMessageNotes(''); }} className="ms-auto h-9 px-4 bg-[#EEF0F3] text-[#6B7280] rounded-lg text-sm font-semibold">{lang === 'ar' ? 'إغلاق' : 'Close'}</button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Package Modal ────────────────────────────────────────────────────── */}
      <Modal isOpen={packageModal} onClose={() => { setPackageModal(false); setEditPackage(null); }} title={editPackage ? (lang === 'ar' ? 'تعديل الباقة' : 'Edit Package') : (lang === 'ar' ? 'باقة جديدة' : 'New Package')} size="md">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="swekt-form-group">
              <label className="swekt-form-label required">اسم الباقة (عربي)</label>
              <input type="text" value={pkgForm.name_ar || ''} onChange={e => setPkgForm(p => ({ ...p, name_ar: e.target.value }))} className="swekt-input" placeholder="باقة الاحترافية" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label">اسم الباقة (إنجليزي)</label>
              <input type="text" value={pkgForm.name_en || ''} onChange={e => setPkgForm(p => ({ ...p, name_en: e.target.value }))} className="swekt-input" placeholder="Professional" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label required">السعر الشهري (ر.س)</label>
              <input type="number" value={pkgForm.price_monthly || 0} onChange={e => setPkgForm(p => ({ ...p, price_monthly: parseFloat(e.target.value) || 0 }))} className="swekt-input" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label">السعر السنوي (ر.س)</label>
              <input type="number" value={pkgForm.price_yearly || 0} onChange={e => setPkgForm(p => ({ ...p, price_yearly: parseFloat(e.target.value) || 0 }))} className="swekt-input" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label">السعر بالدولار ($)</label>
              <input type="number" value={pkgForm.price_usd_monthly || 0} onChange={e => setPkgForm(p => ({ ...p, price_usd_monthly: parseFloat(e.target.value) || 0 }))} className="swekt-input" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label">السعر باليورو (€)</label>
              <input type="number" value={pkgForm.price_eur_monthly || 0} onChange={e => setPkgForm(p => ({ ...p, price_eur_monthly: parseFloat(e.target.value) || 0 }))} className="swekt-input" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label">عدد المستخدمين</label>
              <input type="number" value={pkgForm.max_users || 5} onChange={e => setPkgForm(p => ({ ...p, max_users: parseInt(e.target.value) || 5 }))} className="swekt-input" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label">عدد الفروع</label>
              <input type="number" value={pkgForm.max_branches || 1} onChange={e => setPkgForm(p => ({ ...p, max_branches: parseInt(e.target.value) || 1 }))} className="swekt-input" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={pkgForm.is_active !== false} onChange={e => setPkgForm(p => ({ ...p, is_active: e.target.checked }))} className="w-4 h-4" />
              <span className="text-sm font-semibold">{lang === 'ar' ? 'نشط' : 'Active'}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={pkgForm.is_popular || false} onChange={e => setPkgForm(p => ({ ...p, is_popular: e.target.checked }))} className="w-4 h-4" />
              <span className="text-sm font-semibold">{lang === 'ar' ? 'الأكثر طلباً' : 'Most Popular'}</span>
            </label>
          </div>
          <div className="flex items-center justify-end gap-3">
            <button onClick={() => { setPackageModal(false); setEditPackage(null); }} className="h-9 px-4 bg-[#EEF0F3] text-[#6B7280] rounded-lg text-sm font-semibold">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</button>
            <button onClick={handleSavePackage} disabled={pkgSaving} className="flex items-center gap-1.5 h-9 px-4 bg-[#0F5C5C] text-white rounded-lg text-sm font-bold hover:bg-[#0A3D3D] transition-colors disabled:opacity-70">
              {pkgSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {editPackage ? (lang === 'ar' ? 'تحديث' : 'Update') : (lang === 'ar' ? 'إضافة' : 'Add')}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Notification Modal ───────────────────────────────────────────────── */}
      <Modal isOpen={notifModal} onClose={() => setNotifModal(false)} title={lang === 'ar' ? 'إرسال إشعار عام' : 'Send Broadcast Notification'} size="sm">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">{lang === 'ar' ? 'نص الإشعار' : 'Notification Message'}</label>
            <textarea
              value={notifMessage}
              onChange={e => setNotifMessage(e.target.value)}
              rows={4}
              placeholder={lang === 'ar' ? 'اكتب رسالة الإشعار لجميع الشركات...' : 'Write notification message for all companies...'}
              className="w-full bg-[#F0F2F5] border border-[#E2E6EA] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C]"
            />
          </div>
          <div className="flex items-center gap-2 justify-end">
            <button onClick={() => setNotifModal(false)} className="h-9 px-4 bg-[#EEF0F3] text-[#6B7280] rounded-lg text-sm font-semibold">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</button>
            <button
              onClick={handleSendNotification}
              disabled={!notifMessage.trim() || notifSending}
              className="flex items-center gap-1.5 h-9 px-4 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {notifSending ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
              {lang === 'ar' ? 'إرسال للجميع' : 'Send to All'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Edit Company Modal ───────────────────────────────────────────────── */}
      <Modal isOpen={editCompanyModal} onClose={() => { setEditCompanyModal(false); setEditCompanyForm({}); }} title={lang === 'ar' ? 'تعديل بيانات الشركة — Edit Company' : 'Edit Company Data'} size="md">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="swekt-form-group">
              <label className="swekt-form-label">{lang === 'ar' ? 'اسم الشركة (عربي)' : 'Company Name (Arabic)'}</label>
              <input type="text" value={editCompanyForm.name_ar || ''} onChange={e => setEditCompanyForm(p => ({ ...p, name_ar: e.target.value }))} className="swekt-input" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label">{lang === 'ar' ? 'اسم الشركة (إنجليزي)' : 'Company Name (English)'}</label>
              <input type="text" value={editCompanyForm.name_en || ''} onChange={e => setEditCompanyForm(p => ({ ...p, name_en: e.target.value }))} className="swekt-input" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label">{lang === 'ar' ? 'السجل التجاري' : 'CR Number'}</label>
              <input type="text" value={editCompanyForm.cr_number || ''} onChange={e => setEditCompanyForm(p => ({ ...p, cr_number: e.target.value }))} className="swekt-input" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label">{lang === 'ar' ? 'الرقم الضريبي' : 'VAT Number'}</label>
              <input type="text" value={editCompanyForm.vat_number || ''} onChange={e => setEditCompanyForm(p => ({ ...p, vat_number: e.target.value }))} className="swekt-input" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label">{lang === 'ar' ? 'المدينة' : 'City'}</label>
              <input type="text" value={editCompanyForm.city || ''} onChange={e => setEditCompanyForm(p => ({ ...p, city: e.target.value }))} className="swekt-input" />
            </div>
            <div className="swekt-form-group">
              <label className="swekt-form-label">{lang === 'ar' ? 'الهاتف' : 'Phone'}</label>
              <input type="text" value={editCompanyForm.phone || ''} onChange={e => setEditCompanyForm(p => ({ ...p, phone: e.target.value }))} className="swekt-input" />
            </div>
          </div>

          {/* ── Platform Owner: Limits Management ── */}
          <div className="border-t border-[#E2E6EA] pt-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center">
                <Crown size={12} className="text-purple-700" />
              </div>
              <p className="text-sm font-bold text-[#0D1117]">
                {lang === 'ar' ? 'تعديل حدود الشركة (مدير المنصة)' : 'Company Limits (Platform Owner)'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="swekt-form-group">
                <label className="swekt-form-label flex items-center gap-1.5">
                  <Users size={12} className="text-[#6B7280]" />
                  {lang === 'ar' ? 'الحد الأقصى للمستخدمين' : 'Max Users'}
                </label>
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={editCompanyForm.max_users ?? 5}
                  onChange={e => setEditCompanyForm(p => ({ ...p, max_users: parseInt(e.target.value) || 1 }))}
                  className="swekt-input"
                />
              </div>
              <div className="swekt-form-group">
                <label className="swekt-form-label flex items-center gap-1.5">
                  <Building2 size={12} className="text-[#6B7280]" />
                  {lang === 'ar' ? 'الحد الأقصى للفروع' : 'Max Branches'}
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={editCompanyForm.max_branches ?? 1}
                  onChange={e => setEditCompanyForm(p => ({ ...p, max_branches: parseInt(e.target.value) || 1 }))}
                  className="swekt-input"
                />
              </div>
            </div>
            <p className="text-xs text-[#9CA3AF] mt-1">
              {lang === 'ar' ?'هذه الحدود تتجاوز حدود الباقة الافتراضية وتُطبق فوراً على الشركة' :'These limits override the default package limits and apply immediately to the company'}
            </p>
          </div>

          <div className="flex items-center gap-2 justify-end">
            <button onClick={() => { setEditCompanyModal(false); setEditCompanyForm({}); }} className="h-9 px-4 bg-[#EEF0F3] text-[#6B7280] rounded-lg text-sm font-semibold">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</button>
            <button
              onClick={async () => {
                await handleEditCompany();
                if (editCompanyForm.id && editCompanyForm.max_users && editCompanyForm.max_branches) {
                  await handleUpdateCompanyLimits(
                    editCompanyForm.id,
                    editCompanyForm.max_users,
                    editCompanyForm.max_branches ?? 1,
                  );
                }
              }}
              disabled={editCompanySaving}
              className="flex items-center gap-1.5 h-9 px-4 bg-[#0F5C5C] text-white rounded-lg text-sm font-bold hover:bg-[#0A3D3D] disabled:opacity-50 transition-colors"
            >
              {editCompanySaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {lang === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Delete Confirm ───────────────────────────────────────────────────── */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <AlertTriangle size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-[#0D1117]">{lang === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}</h3>
                <p className="text-xs text-[#6B7280]">{lang === 'ar' ? 'هذا الإجراء لا يمكن التراجع عنه' : 'This action cannot be undone'}</p>
              </div>
            </div>
            <p className="text-sm text-[#6B7280] mb-5">{lang === 'ar' ? 'هل أنت متأكد من حذف هذه الشركة نهائياً؟ سيتم حذف جميع البيانات المرتبطة بها.' : 'Are you sure you want to permanently delete this company? All associated data will be deleted.'}</p>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => setDeleteConfirmId(null)} className="h-9 px-4 bg-[#EEF0F3] text-[#6B7280] rounded-lg text-sm font-semibold">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              <button onClick={() => handleDeleteCompany(deleteConfirmId)} className="h-9 px-4 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 transition-colors">
                {lang === 'ar' ? 'حذف نهائياً' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Change Company Password Modal ────────────────────────────────────── */}
      <Modal
        isOpen={changePasswordModal}
        onClose={() => { setChangePasswordModal(false); setChangePasswordCompany(null); setNewPassword(''); setConfirmPassword(''); }}
        title={lang === 'ar' ? 'تغيير كلمة مرور الشركة' : 'Change Company Password'}
        size="sm"
      >
        {changePasswordCompany && (
          <div className="space-y-4">
            {/* Company info */}
            <div className="flex items-center gap-3 bg-[#EAF4F4] border border-[#B2D8D8] rounded-xl p-3">
              <div className="w-9 h-9 rounded-xl bg-[#0F5C5C] flex items-center justify-center text-white font-bold text-sm shrink-0">
                {changePasswordCompany.name_ar?.charAt(0) || '؟'}
              </div>
              <div>
                <p className="text-sm font-bold text-[#0D1117]">{lang === 'ar' ? changePasswordCompany.name_ar : (changePasswordCompany.name_en || changePasswordCompany.name_ar)}</p>
                <p className="text-xs text-[#6B7280]">{changePasswordCompany.email || changePasswordCompany.cr_number}</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                {lang === 'ar' ?'سيتم تغيير كلمة مرور المدير الرئيسي للشركة مباشرةً. يرجى إبلاغ المدير بكلمة المرور الجديدة.' :'This will directly change the password of the company\'s main admin. Please inform the admin of the new password.'}
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">
                  {lang === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder={lang === 'ar' ? 'أدخل كلمة المرور الجديدة (8 أحرف على الأقل)' : 'Enter new password (min 8 chars)'}
                    className="w-full h-10 bg-[#F0F2F5] border border-[#E2E6EA] rounded-xl px-3 pe-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(p => !p)}
                    className="absolute top-1/2 -translate-y-1/2 end-3 text-[#9CA3AF] hover:text-[#6B7280]"
                  >
                    <Eye size={14} />
                  </button>
                </div>
                {newPassword.length > 0 && newPassword.length < 8 && (
                  <p className="text-xs text-red-500 mt-1">{lang === 'ar' ? 'كلمة المرور قصيرة جداً' : 'Password too short'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0D1117] mb-1.5">
                  {lang === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder={lang === 'ar' ? 'أعد إدخال كلمة المرور' : 'Re-enter password'}
                  className="w-full h-10 bg-[#F0F2F5] border border-[#E2E6EA] rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C]"
                />
                {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">{lang === 'ar' ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match'}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => { setChangePasswordModal(false); setChangePasswordCompany(null); setNewPassword(''); setConfirmPassword(''); }}
                className="h-9 px-4 bg-[#EEF0F3] text-[#6B7280] rounded-lg text-sm font-semibold"
              >
                {lang === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={handleChangeCompanyPassword}
                disabled={changePasswordLoading || newPassword.length < 8 || newPassword !== confirmPassword}
                className="flex items-center gap-1.5 h-9 px-4 bg-[#0F5C5C] text-white rounded-lg text-sm font-bold hover:bg-[#0A3D3D] disabled:opacity-50 transition-colors"
              >
                {changePasswordLoading ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
                {lang === 'ar' ? 'تغيير كلمة المرور' : 'Change Password'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
